import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import {
    get_undistributed_products,
    getRawMaterials,
    get_product_details,
    getBankBalance,
    distribute_undistributed_product,
    getUnits,
    getProductMaster,
    list_raw_material_transactions,
    create_product_details,
    get_product_processing,
    transfer_end_product_quantity
} from "../../../Utility/Api";
import { WarehouseContext } from "../../../Utility/UserContext";
import { NaviGate, getStoredEntriesPerPage, getFormattedCurrentDate2 } from "../../../Utility/function";
import { Pagination } from "../../Common/Functions";
import moment from 'moment';

const convertToGrams = (value, unit) => {
    const val = parseFloat(value || 0);
    if (!unit) return val;
    const normalized = unit.toString().toLowerCase().trim();

    switch (normalized) {
        case "kg":
        case "kilogram":
        case "kilograms":
        case "1":
            return val * 1000;
        case "mg":
        case "milligram":
        case "milligrams":
            return val / 1000;
        case "g":
        case "gram":
        case "grams":
            return val;
        default:
            return val;
    }
};

const extractPackagingWeightInGrams = (title) => {
    if (!title) return null;
    const t = title.trim();

    const mKg = t.match(/(\d+(?:\.\d+)?)\s*(?:kg|kgs|kilogram|kilograms)\b/i);
    if (mKg) {
        return parseFloat(mKg[1]) * 1000;
    }

    const mG = t.match(/(\d+(?:\.\d+)?)\s*(?:grams?|gm|g)\b/i);
    if (mG) {
        return parseFloat(mG[1]);
    }

    const mLead = t.match(/^(\d+(?:\.\d+)?)\b/);
    if (mLead) {
        const val = parseFloat(mLead[1]);
        if ([1, 2, 5].includes(val)) {
            return val * 1000;
        }
        return val;
    }

    const mAny = t.match(/(\d+(?:\.\d+)?)\b/);
    if (mAny) {
        const val = parseFloat(mAny[1]);
        if ([1, 2, 5].includes(val)) {
            return val * 1000;
        }
        return val;
    }

    return null;
};

const filterProductSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: "38px",
        height: "38px",
        fontSize: "13px",
        borderRadius: "0.375rem",
        borderColor: state.isFocused ? "#0d6efd" : "#dee2e6",
        boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)" : "none",
        backgroundColor: "#ffffff",
        cursor: "pointer",
        fontFamily: "Poppins, sans-serif",
        "&:hover": {
            borderColor: state.isFocused ? "#0d6efd" : "#ced4da"
        }
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: "38px",
        padding: "0 8px",
        display: "flex",
        alignItems: "center",
        overflow: "hidden"
    }),
    input: (provided) => ({
        ...provided,
        margin: "0px",
        padding: "0px",
        fontSize: "13px",
        fontFamily: "Poppins, sans-serif"
    }),
    clearIndicator: (provided) => ({
        ...provided,
        padding: "2px 4px",
        cursor: "pointer"
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: "2px 4px",
        cursor: "pointer"
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: "38px"
    }),
    placeholder: (provided) => ({
        ...provided,
        color: "#6c757d",
        fontSize: "13px",
        fontFamily: "Poppins, sans-serif"
    }),
    singleValue: (provided) => ({
        ...provided,
        color: "#212529",
        fontSize: "13px",
        fontWeight: "500",
        fontFamily: "Poppins, sans-serif"
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 9999,
        fontSize: "13px",
        borderRadius: "0.375rem",
        boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
        fontFamily: "Poppins, sans-serif"
    }),
    option: (provided, state) => ({
        ...provided,
        fontSize: "13px",
        backgroundColor: state.isSelected
            ? "#0d6efd"
            : state.isFocused
                ? "#e9ecef"
                : "#ffffff",
        color: state.isSelected ? "#ffffff" : "#212529",
        cursor: "pointer",
        fontFamily: "Poppins, sans-serif",
        "&:active": {
            backgroundColor: "#0d6efd",
            color: "#ffffff"
        }
    })
};

const selectMaterialStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: "31px",
        height: "31px",
        fontSize: "13px",
        borderRadius: "0.25rem",
        borderColor: state.isFocused ? "#0d6efd" : "#dee2e6",
        boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)" : "none",
        backgroundColor: state.isDisabled ? "#e9ecef" : "#ffffff",
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        fontFamily: "Poppins, sans-serif",
        "&:hover": {
            borderColor: state.isFocused ? "#0d6efd" : "#ced4da"
        }
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: "31px",
        padding: "0 8px",
        display: "flex",
        alignItems: "center"
    }),
    input: (provided) => ({
        ...provided,
        margin: "0px",
        padding: "0px",
        fontSize: "13px",
        fontFamily: "Poppins, sans-serif"
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: "31px"
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: "2px 6px"
    }),
    clearIndicator: (provided) => ({
        ...provided,
        padding: "2px 6px"
    }),
    placeholder: (provided) => ({
        ...provided,
        color: "#6c757d",
        fontSize: "13px",
        fontFamily: "Poppins, sans-serif"
    }),
    singleValue: (provided) => ({
        ...provided,
        color: "#212529",
        fontSize: "13px",
        fontFamily: "Poppins, sans-serif"
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 9999,
        fontSize: "13px",
        borderRadius: "0.375rem",
        boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
        fontFamily: "Poppins, sans-serif"
    }),
    option: (provided, state) => ({
        ...provided,
        fontSize: "13px",
        backgroundColor: state.isSelected
            ? "#0d6efd"
            : state.isFocused
                ? "#e9ecef"
                : "#ffffff",
        color: state.isDisabled
            ? "#adb5bd"
            : state.isSelected
                ? "#ffffff"
                : "#212529",
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        fontFamily: "Poppins, sans-serif",
        "&:active": {
            backgroundColor: state.isDisabled ? "transparent" : "#0d6efd",
            color: state.isDisabled ? "#adb5bd" : "#ffffff"
        }
    })
};

const ProcessStepBar = ({ currentStep, onStepClick }) => {
    const steps = [
        { label: "Create Process", icon: "bi bi-file-earmark-plus" },
        { label: "Manufacturing Details", icon: "bi bi-gear" },
        { label: "Distribution", icon: "bi bi-box-seam" }
    ];

    return (
        <div className="my-1 flex-shrink-0">
            <style>{`
                .stepper-container {
                    position: relative;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    width: 100%;
                    max-width: 650px;
                    margin: 0 auto;
                    padding: 0 0 0.2rem 0;
                }
                .stepper-line {
                    position: absolute;
                    top: 17px;
                    left: 16.67%;
                    right: 16.67%;
                    height: 2px;
                    background-color: #E2E8F0;
                    z-index: 1;
                }
                .stepper-line-progress {
                    position: absolute;
                    top: 0;
                    left: 0;
                    height: 100%;
                    background-color: #3B82F6;
                    transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .stepper-step {
                    position: relative;
                    z-index: 2;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    flex: 1;
                    text-align: center;
                }
                .step-icon-outer {
                    width: 34px;
                    height: 34px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: #FFFFFF;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    border: 1.5px solid #E2E8F0;
                }
                .stepper-step.active .step-icon-outer {
                    border: 2px solid #3B82F6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
                }
                .stepper-step.completed .step-icon-outer {
                    border: 2px solid #10B981;
                }
                .stepper-step.pending .step-icon-outer {
                    border: 1.5px solid #CBD5E1;
                }
                .step-icon-inner {
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.8rem;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .stepper-step.active .step-icon-inner {
                    background-color: #3B82F6;
                    color: #FFFFFF;
                }
                .stepper-step.completed .step-icon-inner {
                    background-color: #10B981;
                    color: #FFFFFF;
                }
                .stepper-step.pending .step-icon-inner {
                    background-color: transparent;
                    color: #94A3B8;
                }
                .step-text {
                    margin-top: 4px;
                    font-size: 0.68rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    color: #94A3B8;
                    transition: all 0.3s ease;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    line-height: 1;
                }
                .stepper-step.active .step-text {
                    color: #3B82F6;
                }
                .stepper-step.completed .step-text {
                    color: #10B981;
                }
                .active-dot {
                    width: 4px;
                    height: 4px;
                    border-radius: 50%;
                    background-color: #3B82F6;
                    margin-top: 2px;
                    animation: activePulse 2s infinite ease-in-out;
                }
                @keyframes activePulse {
                    0% { transform: scale(0.8); opacity: 0.5; }
                    50% { transform: scale(1.3); opacity: 1; }
                    100% { transform: scale(0.8); opacity: 0.5; }
                }
            `}</style>

            <div className="stepper-container">
                <div className="stepper-line">
                    <div
                        className="stepper-line-progress"
                        style={{
                            width: `${currentStep === 1 ? 0 : currentStep === 2 ? 50 : 100}%`
                        }}
                    ></div>
                </div>

                {steps.map((step, index) => {
                    const stepNumber = index + 1;
                    const isActive = stepNumber === currentStep;
                    const isCompleted = stepNumber < currentStep;

                    let stepClass = "pending";
                    if (isActive) stepClass = "active";
                    else if (isCompleted) stepClass = "completed";

                    return (
                        <div
                            key={index}
                            className={`stepper-step ${stepClass}`}
                            onClick={() => onStepClick && onStepClick(stepNumber)}
                            style={{ cursor: onStepClick ? 'pointer' : 'default' }}
                        >
                            <div className="step-icon-outer">
                                <div className="step-icon-inner">
                                    {isCompleted ? (
                                        <i className="bi bi-check-lg" style={{ fontSize: "1.1rem" }}></i>
                                    ) : (
                                        <i className={step.icon}></i>
                                    )}
                                </div>
                            </div>
                            <div className="step-text">
                                <span>{step.label}</span>
                                {isActive && <div className="active-dot"></div>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const formatDateDisplay = (dateVal) => {
    if (!dateVal) return "";
    const m = moment(dateVal);
    return m.isValid() ? m.format('DD/MM/YYYY') : dateVal;
};

const FormattedDateInput = ({ name, value, onChange, min, max, className, required, isInvalid, disabled }) => {
    const inputRef = useRef(null);

    const handleClick = () => {
        if (disabled) return;
        try {
            if (inputRef.current && typeof inputRef.current.showPicker === 'function') {
                inputRef.current.showPicker();
            }
        } catch (err) {}
    };

    return (
        <div
            className="position-relative w-100"
            style={{ pointerEvents: disabled ? 'none' : 'auto', opacity: disabled ? 0.7 : 1 }}
            onClick={handleClick}
        >
            <style>{`
                .custom-full-date-picker::-webkit-calendar-picker-indicator {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    width: 100%;
                    height: 100%;
                    opacity: 0;
                    cursor: pointer;
                }
            `}</style>
            <input
                ref={inputRef}
                type="date"
                name={name}
                className="custom-full-date-picker"
                value={value || ''}
                min={min}
                max={max}
                required={required}
                disabled={disabled}
                onChange={onChange}
                onClick={(e) => {
                    try {
                        if (typeof e.target.showPicker === 'function') {
                            e.target.showPicker();
                        }
                    } catch (err) {}
                }}
                style={{
                    opacity: 0,
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    zIndex: 10
                }}
            />
            <div
                className={`form-control d-flex align-items-center justify-content-between ${isInvalid ? 'is-invalid' : ''} ${className || ''}`}
                style={{ cursor: disabled ? 'not-allowed' : 'pointer', minHeight: '38px', backgroundColor: disabled ? '#f1f5f9' : '#fff' }}
            >
                <span className={value ? "text-dark font-medium" : "text-muted"}>
                    {value ? (moment(value).isValid() ? moment(value).format('DD/MM/YYYY') : value) : 'DD/MM/YYYY'}
                </span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-secondary">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
            </div>
        </div>
    );
};

const getMfgDateDisplay = (dates) => {
    const unique = [...new Set(dates.filter(Boolean))].sort();
    if (unique.length === 0) return "-";
    if (unique.length === 1) return formatDateDisplay(unique[0]);
    return `${formatDateDisplay(unique[0])} to ${formatDateDisplay(unique[unique.length - 1])}`;
};

const getExpiryDateDisplay = (dates) => {
    const unique = [...new Set(dates.filter(Boolean))].sort();
    if (unique.length === 0) return "-";
    if (unique.length === 1) return formatDateDisplay(unique[0]);
    return `${formatDateDisplay(unique[0])} to ${formatDateDisplay(unique[unique.length - 1])}`;
};

const formatBatchNo = (batches) => {
    if (!batches || !Array.isArray(batches) || batches.length === 0) return "";
    if (batches.length <= 3) {
        return batches.map(b => b.batch_no).join(", ");
    }
    const visible = batches.slice(0, 3).map(b => b.batch_no).join(", ");
    return `${visible}... (+${batches.length - 3} more)`;
};

const UndistributedProducts = () => {
    const { showLoader, hideLoader, shopItem, UserItem, warehouse } = useContext(WarehouseContext);

    // List State
    const [productsList, setProductsList] = useState([]);
    const [productMasterList, setProductMasterList] = useState([]);

    const groupedProducts = useMemo(() => {
        const groups = {};
        productsList.forEach(batch => {
            const pid = batch.p_id;
            if (!groups[pid]) {
                groups[pid] = {
                    p_id: pid,
                    product_title: batch.product_title,
                    units: batch.units,
                    batches: [],
                    total_qty: 0,
                    total_remaining: 0,
                    mfg_dates: [],
                    expiry_dates: []
                };
            }
            groups[pid].batches.push(batch);
            groups[pid].total_qty += parseFloat(batch.qty || 0);
            groups[pid].total_remaining += parseFloat(batch.remaining || 0);
            if (batch.mfg_date) groups[pid].mfg_dates.push(batch.mfg_date);
            if (batch.expiry_date) groups[pid].expiry_dates.push(batch.expiry_date);
        });
        return Object.values(groups);
    }, [productsList]);

    const [selectedFilterProduct, setSelectedFilterProduct] = useState(null);

    // Dynamic product filter options with type-to-search support
    const productFilterOptions = useMemo(() => {
        const productMap = new Map();

        // 1. Add from productMasterList
        if (Array.isArray(productMasterList)) {
            productMasterList.forEach(p => {
                const id = p.product_id || p.id;
                if (id) {
                    productMap.set(String(id), p.title || `Product #${id}`);
                }
            });
        }

        // 2. Add from productsList
        if (Array.isArray(productsList)) {
            productsList.forEach(batch => {
                if (batch && batch.p_id && !productMap.has(String(batch.p_id))) {
                    productMap.set(String(batch.p_id), batch.product_title || `Product #${batch.p_id}`);
                }
            });
        }

        const options = Array.from(productMap.entries()).map(([id, title]) => ({
            value: id,
            label: title
        }));

        options.sort((a, b) => a.label.localeCompare(b.label));
        return options;
    }, [productMasterList, productsList]);

    const filteredGroupedProducts = useMemo(() => {
        if (!selectedFilterProduct || !selectedFilterProduct.value) {
            return groupedProducts;
        }
        return groupedProducts.filter(item =>
            String(item.p_id) === String(selectedFilterProduct.value) ||
            (item.product_title && selectedFilterProduct.label && item.product_title.trim().toLowerCase() === selectedFilterProduct.label.trim().toLowerCase())
        );
    }, [groupedProducts, selectedFilterProduct]);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(getStoredEntriesPerPage(10));

    useEffect(() => {
        const calculatedTotal = Math.max(1, Math.ceil(filteredGroupedProducts.length / entriesPerPage));
        if (currentPage > calculatedTotal && calculatedTotal > 0) {
            setCurrentPage(1);
        }
    }, [currentPage, filteredGroupedProducts, entriesPerPage]);

    // Modal State
    const [showDistributeModal, setShowDistributeModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [transferSourceBatch, setTransferSourceBatch] = useState(null);
    const [transferSourceProduct, setTransferSourceProduct] = useState(null);
    const [transferTargetProduct, setTransferTargetProduct] = useState(null);
    const [transferQty, setTransferQty] = useState("");
    const [transferDate, setTransferDate] = useState(getFormattedCurrentDate2());
    const [transferType, setTransferType] = useState("partial");
    const [transferRemarks, setTransferRemarks] = useState("");
    const [isTransferring, setIsTransferring] = useState(false);
    const [transferError, setTransferError] = useState("");
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [rawMaterialsList, setRawMaterialsList] = useState([]);
    const [unitsList, setUnitsList] = useState([]);
    const [availableBankBalance, setAvailableBankBalance] = useState(0);
    const [distributionItems, setDistributionItems] = useState([]);
    const [availableProductDetails, setAvailableProductDetails] = useState([]);

    // Modal Form State (Copied exactly from Product Process Render Step 2)
    const [selectedPackagingId, setSelectedPackagingId] = useState("");
    const [selectedPackagingQty, setSelectedPackagingQty] = useState("");
    const [selectedPackagingPrice, setSelectedPackagingPrice] = useState("");
    const [processDetails, setProcessDetails] = useState(null);

    const [expenseItems, setExpenseItems] = useState([
        { id: 1, description: 'Labour Charges', amount: 0 },
        { id: 2, description: 'Package Charges', amount: 0 },
        { id: 3, description: 'EB/Rent Charges', amount: 0 },
        { id: 4, description: 'Other Charges', amount: 0 }
    ]);
    const [customExpenses, setCustomExpenses] = useState([]);

    const labourCharges = useMemo(() => expenseItems.find(i => i.description === 'Labour Charges')?.amount || 0, [expenseItems]);
    const packageCharges = useMemo(() => expenseItems.find(i => i.description === 'Package Charges')?.amount || 0, [expenseItems]);
    const ebRentCharges = useMemo(() => expenseItems.find(i => i.description === 'EB/Rent Charges')?.amount || 0, [expenseItems]);
    const otherChargesVal = useMemo(() => expenseItems.find(i => i.description === 'Other Charges')?.amount || 0, [expenseItems]);

    const customExpensesTotal = useMemo(() => {
        return customExpenses.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    }, [customExpenses]);

    const handleExpenseChange = (id, value) => {
        const updatedItems = expenseItems.map(item =>
            item.id === id ? { ...item, amount: value } : item
        );
        setExpenseItems(updatedItems);
    };

    // Stepper and ingredients states
    const [modalStep, setModalStep] = useState(3);
    const [ingredientsList, setIngredientsList] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);

    // Raw materials consumed for process in Step 2, and new Package Size modal states
    const [rawMaterialsListForProcess, setRawMaterialsListForProcess] = useState([]);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [formData1, setFormData1] = useState({
        p_id: "",
        p_code: "",
        p_weight: "",
        pbox_unit: "",
        p_price: "",
        pbox_qty: "",
        pbox_code: "",
        pbox_price: ""
    });
    const [errorss, setErrorss] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchUndistributedProducts();
        fetchRawMaterials();
        fetchUnits();
        fetchProductMasterList();
    }, [shopItem, warehouse]);

    useEffect(() => {
        if (selectedBatch && productMasterList.length > 0) {
            fetchIngredientsFromProductMaster(selectedBatch.p_id);
        }
    }, [selectedBatch, productMasterList, rawMaterialsList]);

    const fetchUndistributedProducts = async () => {
        showLoader();
        try {
            const params = {
                shop_id: shopItem?.shop_id || shopItem?.id || "0",
                warehouse_id: warehouse?.id || "1"
            };
            const res = await get_undistributed_products(params);
            if (res.data && res.data.status === "success") {
                setProductsList(res.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching undistributed products:", error);
            toast.error("Failed to load undistributed products");
        } finally {
            hideLoader();
        }
    };

    const fetchRawMaterials = async () => {
        try {
            const res = await getRawMaterials();
            if (res.data && res.data.data) {
                setRawMaterialsList(res.data.data);
                return res.data.data;
            }
            return [];
        } catch (error) {
            console.error("Error fetching raw materials:", error);
            return [];
        }
    };

    // Auto-refresh packaging materials and products whenever distribute modal opens
    useEffect(() => {
        if (showDistributeModal) {
            fetchRawMaterials();
            fetchProductMasterList();
        }
    }, [showDistributeModal]);

    const fetchUnits = async () => {
        try {
            const res = await getUnits();
            if (res.data && res.data.data) {
                setUnitsList(res.data.data);
            }
        } catch (error) {
            console.error("Error fetching units:", error);
        }
    };

    const getAvailableUnits = useMemo(() => {
        const list = [...unitsList];
        if (!list.some(u => String(u.title).toLowerCase() === "pcs")) {
            list.push({ id: "pcs", title: "pcs" });
        }
        return list;
    }, [unitsList]);

    const unitMap = useMemo(() => {
        const map = {};
        unitsList.forEach(u => {
            map[String(u.id)] = u.title;
        });
        map["pcs"] = "pcs";
        return map;
    }, [unitsList]);

    const packMaterialOptions = useMemo(() => {
        const currentPId = String(selectedBatch?.p_id || selectedProduct?.p_id || "").trim();
        const currentProd = (productsList || []).find(p => String(p.product_id) === currentPId);
        const currentProdTitle = (currentProd?.title || selectedProduct?.title || selectedBatch?.product_title || "").trim().toLowerCase();

        // 1. Get all configured product packs for the current product (excluding FREE_SIZE)
        const relevantPacks = (availableProductDetails || []).filter(detail =>
            String(detail.p_id) === currentPId &&
            detail.id !== "FREE_SIZE" &&
            detail.p_code !== "FREE_SIZE" &&
            detail.pbox_code !== "FREE SIZE / LOSE QTY"
        );

        const packsToUse = relevantPacks.length > 0
            ? relevantPacks
            : (distributionItems || []).filter(item =>
                item.id !== "FREE_SIZE" &&
                item.p_code !== "FREE_SIZE" &&
                item.pbox_code !== "FREE SIZE / LOSE QTY"
            );

        const allowedPacks = packsToUse.map(pack => {
            const rawWeight = parseFloat(pack.p_weight || 0);
            const unit = pack.pbox_unit || "grams";
            return {
                id: pack.id,
                weight: rawWeight,
                unit: unit,
                grams: convertToGrams(rawWeight, unit),
                label: `${rawWeight} ${unit}`
            };
        }).filter(p => p.grams > 0);

        if (allowedPacks.length === 0) {
            return [];
        }

        // Filter packaging materials (rm.type === "1") solely by matching weight numbers with allowed product packs
        const filtered = (rawMaterialsList || [])
            .filter(rm => String(rm.type) === "1")
            .filter(rm => {
                const matTitle = (rm.title || "").trim();
                const matWeight = extractPackagingWeightInGrams(matTitle);
                if (!matWeight) return false;

                // MUST match one of the available product pack weights!
                return allowedPacks.some(pack => Math.abs(pack.grams - matWeight) <= 1);
            })
            .map(rm => {
                const stock = parseFloat(rm.remaining_stock || 0);
                const unit = unitMap[String(rm.unit_id)] || rm.unit_id || 'pcs';
                const matWeight = extractPackagingWeightInGrams(rm.title);
                const matchingPack = allowedPacks.find(pack => Math.abs(pack.grams - matWeight) <= 1);
                return {
                    value: String(rm.id),
                    label: `${rm.title} (Stock: ${stock.toFixed(2)} ${unit})`,
                    packGrams: matWeight,
                    packId: matchingPack ? matchingPack.id : null,
                    isDisabled: stock <= 0
                };
            });

        return filtered;
    }, [rawMaterialsList, unitMap, availableProductDetails, productsList, selectedBatch, selectedProduct, distributionItems]);

    // State for New Distribution Flow (Matching Product_process.js Step 3)
    const [newDistributionRows, setNewDistributionRows] = useState([
        {
            id: 1,
            product_qty: "",
            product_unit: "1",
            packaging_id: "",
            pack_qty: "",
            pack_unit: "pcs",
            wastage_val: "",
            wastage_unit: "pcs",
            pack_qty_edited: false
        }
    ]);

    const addNewDistributionRow = () => {
        const kgId = getAvailableUnits.find(u => u.title?.toUpperCase() === 'KG' || u.title?.toLowerCase() === 'kg')?.id || "1";
        const pcsId = getAvailableUnits.find(u => u.title?.toLowerCase() === 'pcs')?.id || "pcs";

        const newId = newDistributionRows.length > 0
            ? Math.max(...newDistributionRows.map(r => r.id || 0)) + 1
            : 1;
        setNewDistributionRows([
            ...newDistributionRows,
            {
                id: newId,
                product_qty: "",
                product_unit: kgId,
                packaging_id: "",
                pack_qty: "",
                pack_unit: pcsId,
                wastage_val: "",
                wastage_unit: pcsId,
                pack_qty_edited: false
            }
        ]);
    };

    const removeNewDistributionRow = (index) => {
        if (newDistributionRows.length === 1) {
            toast.info("At least one row is required in the distribution flow.");
            return;
        }
        const updated = newDistributionRows.filter((_, i) => i !== index);
        setNewDistributionRows(updated);
    };

    const calculateAutoWastage = (row) => {
        if (!row || !row.packaging_id || !row.product_qty || !row.pack_qty) return null;

        const mat = rawMaterialsList.find(rm => String(rm.id) === String(row.packaging_id));
        if (!mat || !mat.title) return null;

        const match = mat.title.match(/(\d+(?:\.\d+)?)\s*(grams?|g|kgs?|kilograms?)?/i);
        if (!match) return null;

        let coverGrams = parseFloat(match[1]);
        const coverGramUnit = (match[2] || "gram").toLowerCase();
        if (coverGramUnit.startsWith("kg")) {
            coverGrams = coverGrams * 1000;
        }
        if (coverGrams <= 0) return null;

        let pQty = parseFloat(row.product_qty || 0);
        const pUnitTitle = (unitMap[String(row.product_unit)] || row.product_unit || "kg").toString().toLowerCase();
        let bulkGrams = pQty;
        if (pUnitTitle === "kg" || pUnitTitle === "kilogram" || pUnitTitle === "l" || pUnitTitle === "litre" || pUnitTitle === "li") {
            bulkGrams = pQty * 1000;
        }
        if (bulkGrams <= 0) return null;

        const neededPcs = bulkGrams / coverGrams;

        const pcsPerKg = parseFloat(row.pcs_per_kg || mat.pcs_per_kg || 0);
        const packUnitTitle = (unitMap[String(row.pack_unit)] || row.pack_unit || "pcs").toString().trim().toLowerCase();
        const isKgPackUnit = packUnitTitle === "kg" || packUnitTitle === "kilogram" || packUnitTitle === "1";
        const packQtyVal = parseFloat(row.pack_qty || 0);

        const enteredPcs = isKgPackUnit ? (packQtyVal * pcsPerKg) : packQtyVal;
        if (enteredPcs <= 0) return null;

        const autoWastedPcs = Math.max(0, enteredPcs - neededPcs);

        const wastageUnitRaw = String(row.wastage_unit || "pcs");
        const wastageUnitTitle = (wastageUnitRaw === "₹" || wastageUnitRaw === "rs")
            ? "₹"
            : (unitMap[wastageUnitRaw] || wastageUnitRaw || "pcs").toString().toLowerCase();

        let autoWastageVal = 0;
        if (wastageUnitTitle === "pcs" || wastageUnitTitle === "pack") {
            autoWastageVal = autoWastedPcs;
        } else if (wastageUnitTitle === "kg" || wastageUnitTitle === "1" || wastageUnitTitle === "kilogram" || wastageUnitTitle === "l" || wastageUnitTitle === "litre" || wastageUnitTitle === "li") {
            autoWastageVal = pcsPerKg > 0 ? (autoWastedPcs / pcsPerKg) : ((autoWastedPcs * coverGrams) / 1000);
        } else if (wastageUnitTitle === "gram" || wastageUnitTitle === "g" || wastageUnitTitle === "gm" || wastageUnitTitle === "grams" || wastageUnitTitle === "ml") {
            autoWastageVal = pcsPerKg > 0 ? ((autoWastedPcs / pcsPerKg) * 1000) : (autoWastedPcs * coverGrams);
        } else if (wastageUnitTitle === "₹") {
            const baseCoverPrice = parseFloat(mat.latest_price_unit || mat.price_unit || mat.cost_price || 0);
            const costPerPiece = pcsPerKg > 0 ? (baseCoverPrice / pcsPerKg) : baseCoverPrice;
            autoWastageVal = autoWastedPcs * costPerPiece;
        }

        return {
            neededPcs,
            enteredPcs,
            autoWastedPcs,
            autoWastageVal: parseFloat(autoWastageVal.toFixed(3))
        };
    };

    const handleNewRowChange = (index, field, value) => {
        const updated = [...newDistributionRows];
        updated[index] = { ...updated[index], [field]: value };

        if (field === "wastage_val") {
            updated[index].wastage_val_edited = true;
        }

        if (field === "packaging_id") {
            if (!value) {
                updated[index].pcs_per_kg = "";
                updated[index].pack_qty = "";
                updated[index].pack_unit = "pcs";
                updated[index].pack_qty_edited = false;
                updated[index].wastage_val_edited = false;
            } else {
                const mat = rawMaterialsList.find(rm => String(rm.id) === String(value));
                if (mat) {
                    if (mat.pcs_per_kg !== null && mat.pcs_per_kg !== undefined && mat.pcs_per_kg !== "") {
                        updated[index].pcs_per_kg = String(mat.pcs_per_kg);
                    } else {
                        updated[index].pcs_per_kg = "";
                    }

                    const matUnitTitle = (unitMap[String(mat.unit_id)] || mat.unit_id || "").toString().trim().toLowerCase();
                    if (matUnitTitle === "kg" || matUnitTitle === "1") {
                        const kgUnitId = getAvailableUnits.find(u => u.title?.toUpperCase() === 'KG')?.id || "1";
                        updated[index].pack_unit = kgUnitId;
                        if (!updated[index].pack_qty_edited) {
                            updated[index].pack_qty = "";
                        }
                    } else {
                        const pcsUnitId = getAvailableUnits.find(u => u.title?.toLowerCase() === 'pcs')?.id || "pcs";
                        updated[index].pack_unit = pcsUnitId;
                        updated[index].pack_qty_edited = false;
                    }
                }
            }
        }

        if (field === "packaging_id" || field === "product_qty" || field === "product_unit") {
            const row = updated[index];
            const pMatId = row.packaging_id;
            const pQty = row.product_qty;
            const pUnitId = row.product_unit;

            if (pMatId && pQty) {
                const mat = rawMaterialsList.find(rm => String(rm.id) === String(pMatId));
                if (mat && mat.title) {
                    const matUnitTitle = (unitMap[String(mat.unit_id)] || mat.unit_id || "").toString().trim().toLowerCase();
                    const isKgMat = matUnitTitle === "kg" || matUnitTitle === "1";

                    if (!isKgMat) {
                        const match = mat.title.match(/^(\d+)\s*(grams?|g|kgs?|kilograms?)?/i);
                        if (match) {
                            const coverSizeGrams = convertToGrams(match[1], match[2] || "grams");
                            let bulkGrams = parseFloat(pQty || 0);
                            const unitTitle = (unitMap[String(pUnitId)] || pUnitId || "kg").toString().toLowerCase();
                            if (unitTitle === "kg" || unitTitle === "kilogram" || unitTitle === "litre" || unitTitle === "li") {
                                bulkGrams = bulkGrams * 1000;
                            }
                            if (coverSizeGrams > 0 && bulkGrams > 0) {
                                const calculatedPcs = Math.round(bulkGrams / coverSizeGrams);
                                if (!row.pack_qty_edited) {
                                    updated[index].pack_qty = calculatedPcs.toString();
                                }
                            }
                        }
                    }
                }
            }
        }

        if (field === "pack_qty") {
            updated[index].pack_qty_edited = true;
        }

        if (["packaging_id", "product_qty", "product_unit", "pack_qty", "pack_unit", "wastage_unit"].includes(field)) {
            if (field === "wastage_unit") {
                updated[index].wastage_val_edited = false;
            }
            if (!updated[index].wastage_val_edited) {
                const autoRes = calculateAutoWastage(updated[index]);
                if (autoRes !== null && autoRes.autoWastedPcs >= 0) {
                    updated[index].wastage_val = String(autoRes.autoWastageVal);
                }
            }
        }

        setNewDistributionRows(updated);
    };

    const calculatePerUnitCost = () => {
        if (!selectedBatch) return 0;
        const directPrice = parseFloat(selectedBatch.mfg_price || processDetails?.mfg_price_unit || 0);
        if (directPrice > 0) return directPrice;

        const overallCost = parseFloat(selectedBatch.mfg_cost_overall || 0);
        const totalQty = parseFloat(selectedBatch.qty || selectedBatch.remaining || 0);
        if (overallCost > 0 && totalQty > 0) {
            return overallCost / totalQty;
        }
        return 0;
    };

    const getNewRowCalculations = (row) => {
        const hasProductQty = row.product_qty !== undefined && row.product_qty !== null && String(row.product_qty).trim() !== "" && parseFloat(row.product_qty) > 0;
        const costPerKg = hasProductQty ? calculatePerUnitCost() : 0;
        const pQty = parseFloat(row.product_qty || 0);
        const pUnitTitle = (unitMap[String(row.product_unit)] || row.product_unit || "kg").toString().toLowerCase();
        let qtyInKg = pQty;
        if (pUnitTitle === "gram" || pUnitTitle === "g" || pUnitTitle === "gm" || pUnitTitle === "grams" || pUnitTitle === "ml") {
            qtyInKg = pQty / 1000;
        }

        const productBulkCost = qtyInKg * costPerKg;
        const rowProductCost = productBulkCost;

        let baseCoverPrice = 0;
        let selectedMat = null;
        if (row.packaging_id) {
            selectedMat = rawMaterialsList.find(rm => String(rm.id) === String(row.packaging_id));
            if (selectedMat) {
                baseCoverPrice = parseFloat(selectedMat.latest_price_unit || selectedMat.price_unit || selectedMat.cost_price || 0);
            }
        }

        const packUnitTitle = (unitMap[String(row.pack_unit)] || row.pack_unit || "pcs").toString().trim().toLowerCase();
        const isKgPackUnit = packUnitTitle === "kg" || packUnitTitle === "kilogram" || packUnitTitle === "1";

        const packQtyVal = parseFloat(row.pack_qty || 0);
        const pcsPerKg = parseFloat(row.pcs_per_kg || selectedMat?.pcs_per_kg || 0);
        const costPerPiece = pcsPerKg > 0 ? (baseCoverPrice / pcsPerKg) : baseCoverPrice;

        const calculatedPcs = isKgPackUnit ? (packQtyVal * pcsPerKg) : packQtyVal;
        const packCoverPrice = packQtyVal > 0 ? (calculatedPcs * costPerPiece) : 0;

        const wastageVal = parseFloat(row.wastage_val || 0);
        const wastageUnitRaw = String(row.wastage_unit || "");
        const wastageUnitTitle = (wastageUnitRaw === "₹" || wastageUnitRaw === "rs")
            ? "₹"
            : (unitMap[wastageUnitRaw] || wastageUnitRaw || "gram").toString().toLowerCase();

        let wastageCost = 0;
        if (wastageUnitTitle === "₹") {
            wastageCost = wastageVal;
        } else if (wastageUnitTitle === "gram" || wastageUnitTitle === "g" || wastageUnitTitle === "gm" || wastageUnitTitle === "ml") {
            wastageCost = (wastageVal / 1000) * costPerKg;
        } else if (wastageUnitTitle === "kg" || wastageUnitTitle === "l" || wastageUnitTitle === "litre" || wastageUnitTitle === "li") {
            wastageCost = wastageVal * costPerKg;
        } else if (wastageUnitTitle === "pcs" || wastageUnitTitle === "pack") {
            wastageCost = wastageVal * costPerPiece;
        }

        let wastedPcs = 0;
        if (wastageUnitTitle === "pcs" || wastageUnitTitle === "pack") {
            wastedPcs = wastageVal;
        } else if (wastageUnitTitle === "gram" || wastageUnitTitle === "g" || wastageUnitTitle === "gm" || wastageUnitTitle === "ml") {
            if (pcsPerKg > 0) wastedPcs = (wastageVal / 1000) * pcsPerKg;
        } else if (wastageUnitTitle === "kg" || wastageUnitTitle === "l" || wastageUnitTitle === "litre" || wastageUnitTitle === "li") {
            if (pcsPerKg > 0) wastedPcs = wastageVal * pcsPerKg;
        }

        const usablePcs = Math.max(0, calculatedPcs - wastedPcs);
        const packCoverTotalCost = usablePcs * costPerPiece;
        const overallCosting = rowProductCost + packCoverTotalCost + wastageCost;
        const perPieceCosting = usablePcs > 0
            ? (overallCosting / usablePcs)
            : (calculatedPcs > 0 ? (overallCosting / calculatedPcs) : 0);

        return {
            qtyInKg,
            costPerKg,
            productBulkCost,
            rowProductCost,
            baseCoverPrice,
            packCoverPrice,
            costPerPiece,
            packCoverTotalCost,
            wastageCost,
            overallCosting,
            perPieceCosting,
            isKgPackUnit,
            pcsPerKg,
            packQtyVal,
            calculatedPcs,
            wastedPcs,
            usablePcs
        };
    };

    const calculateNewDistributionSummary = () => {
        const totalBulkKg = parseFloat(selectedBatch?.remaining || selectedBatch?.qty || 0);

        let totalDistributedKg = 0;
        let totalPackCost = 0;
        let totalWastageCost = 0;
        let totalOverallCosting = 0;
        let totalPcs = 0;
        let totalUsablePcs = 0;

        newDistributionRows.forEach(row => {
            const calcs = getNewRowCalculations(row);
            totalDistributedKg += calcs.qtyInKg;
            totalPackCost += calcs.packCoverTotalCost;
            totalWastageCost += calcs.wastageCost;
            totalOverallCosting += calcs.overallCosting;
            totalPcs += calcs.calculatedPcs;
            totalUsablePcs += calcs.usablePcs;
        });

        const remainingKg = totalBulkKg - totalDistributedKg;
        const remainingGrams = remainingKg * 1000;
        const avgPerPieceCost = totalUsablePcs > 0
            ? (totalOverallCosting / totalUsablePcs)
            : (totalPcs > 0 ? (totalOverallCosting / totalPcs) : 0);

        return {
            totalBulkKg,
            totalDistributedKg,
            remainingKg,
            remainingGrams,
            totalPackCost,
            totalWastageCost,
            totalOverallCosting,
            totalPcs,
            totalUsablePcs,
            avgPerPieceCost
        };
    };

    const isDistributionComplete = () => {
        if (!selectedBatch) return false;
        const summary = calculateNewDistributionSummary();
        return summary.totalDistributedKg > 0 && summary.remainingKg >= -0.01;
    };

    const totalPackagingPrice = useMemo(() => {
        const summary = calculateNewDistributionSummary();
        return (summary.totalPackCost + summary.totalWastageCost).toFixed(2);
    }, [newDistributionRows, rawMaterialsList, unitMap]);

    // Calculate total packaging and other charges for distribution cost summary
    const totalDistributionCost = useMemo(() => {
        const lc = parseFloat(labourCharges || 0);
        const er = parseFloat(ebRentCharges || 0);
        const oc = parseFloat(otherChargesVal || 0);
        const ce = parseFloat(customExpensesTotal || 0);
        const pkg = parseFloat(totalPackagingPrice || 0);
        return lc + er + oc + ce + pkg;
    }, [labourCharges, ebRentCharges, otherChargesVal, customExpensesTotal, totalPackagingPrice]);

    const fetchBankBalance = async () => {
        try {
            const shopId = shopItem?.shop_id || shopItem?.id || "";
            const res = await getBankBalance(shopId);
            if (res.data) {
                setAvailableBankBalance(Number(res.data.bank_balance || 0));
            }
        } catch (error) {
            console.error("Error fetching bank balance:", error);
        }
    };

    const fetchProductDetails = async (productId, batchRecord = null) => {
        try {
            const params = {
                p_id: productId,
                shop_id: shopItem?.shop_id || shopItem?.id || ""
            };
            const res = await get_product_details(params);
            if (res.data && res.data.data) {
                setAvailableProductDetails(res.data.data);
                const targetBatch = batchRecord || selectedBatch;
                const targetBulkGrams = targetBatch ? Number(targetBatch.remaining) * 1000 : 0;
                const items = res.data.data.map((detail, idx) => {
                    const weightInGrams = parseFloat(detail.p_weight || 0);
                    return {
                        row_id: `undist_row_${idx}_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
                        id: detail.id,
                        pbox_unit: detail.pbox_unit,
                        product_title: detail.product_title || `${weightInGrams}g Package`,
                        p_weight: weightInGrams,
                        qty: 0,
                        pbox_code: detail.pbox_code,
                        packaging_id: "",
                        packaging_qty: "",
                        packaging_price: "0",
                        packaging_unit: "pcs"
                    };
                });
                const hasFreeSize = items.some(item => item.pbox_code === "FREE SIZE / LOSE QTY");
                if (!hasFreeSize) {
                    const fallbackTitle = items.length > 0 ? items[0].product_title : (targetBatch?.product_title || "Product");
                    items.push({
                        row_id: `undist_row_freesize_${Date.now()}`,
                        id: "FREE_SIZE",
                        pbox_unit: "kilograms",
                        product_title: fallbackTitle,
                        p_weight: 1,
                        qty: 0,
                        pbox_code: "FREE SIZE / LOSE QTY",
                        packaging_id: "",
                        packaging_qty: "",
                        packaging_price: "0",
                        packaging_unit: "pcs"
                    });
                }

                // Calculate suggested distribution based on weights in grams
                const getItemGrams = (item) => convertToGrams(item.p_weight, item.pbox_unit);
                let remainingWeight = targetBulkGrams;
                const distributedItems = [...items];

                while (remainingWeight > 0 && distributedItems.some(item => getItemGrams(item) <= remainingWeight)) {
                    const itemToUse = distributedItems.find(item => getItemGrams(item) <= remainingWeight);
                    if (!itemToUse) break;

                    const itemGrams = getItemGrams(itemToUse);
                    if (!itemGrams || itemGrams <= 0) break;

                    const itemIndex = distributedItems.findIndex(i => i.id === itemToUse.id);
                    const newQty = (distributedItems[itemIndex].qty || 0) + 1;
                    distributedItems[itemIndex].qty = newQty;
                    if (distributedItems[itemIndex].packaging_id) {
                        distributedItems[itemIndex].packaging_qty = newQty.toString();
                    }
                    remainingWeight -= itemGrams;
                }

                if (remainingWeight > 0) {
                    const losePacksIndex = distributedItems.findIndex(item =>
                        item.pbox_code === "FREE SIZE / LOSE QTY" || item.id === "FREE_SIZE"
                    );
                    if (losePacksIndex !== -1) {
                        const itemGrams = getItemGrams(distributedItems[losePacksIndex]);
                        if (itemGrams > 0) {
                            const extraQty = remainingWeight / itemGrams;
                            const newQty = (distributedItems[losePacksIndex].qty || 0) + extraQty;
                            distributedItems[losePacksIndex].qty = parseFloat(newQty.toFixed(3));
                            if (distributedItems[losePacksIndex].packaging_id) {
                                distributedItems[losePacksIndex].packaging_qty = parseFloat(newQty.toFixed(3)).toString();
                            }
                            remainingWeight = 0;
                        }
                    }
                }

                setDistributionItems(distributedItems);
            }
        } catch (error) {
            console.error("Error fetching product packaging details:", error);
        }
    };

    const fetchProductMasterList = async () => {
        try {
            const res = await getProductMaster();
            if (res.data && res.data.data) {
                setProductMasterList(res.data.data);
            }
        } catch (error) {
            console.error("Error fetching product master list:", error);
        }
    };

    const fetchIngredientsFromProductMaster = (productId) => {
        if (!productId) return;
        try {
            const product = productMasterList.find(p => String(p.product_id) === String(productId));
            if (product && product.ingridents) {
                const ingredientsData = JSON.parse(product.ingridents);
                if (Array.isArray(ingredientsData) && ingredientsData.length > 0) {
                    const enrichedIngredients = ingredientsData.map(ing => {
                        const itemType = ing.item_type || "raw";
                        if (itemType === "product") {
                            const prod = productMasterList.find(p => String(p.product_id || p.id) === String(ing.id));
                            return {
                                id: ing.id,
                                item_type: "product",
                                title: prod ? `${prod.title} (Readymade)` : `Product #${ing.id} (Readymade)`,
                                remaining_stock: prod ? (parseFloat(prod.qty || prod.remaining_stock || 0)) : 0,
                                custom_qty: ing.qty || "",
                                custom_unit: ing.unit || ""
                            };
                        } else {
                            const rawMaterial = rawMaterialsList.find(rm => String(rm.id) === String(ing.id));
                            return {
                                id: ing.id,
                                item_type: "raw",
                                title: rawMaterial ? `${rawMaterial.title} (Raw Material)` : `Raw Material #${ing.id}`,
                                remaining_stock: rawMaterial ? rawMaterial.remaining_stock : 0,
                                custom_qty: ing.qty || "",
                                custom_unit: ing.unit || ""
                            };
                        }
                    });
                    setIngredientsList(enrichedIngredients);
                    setSelectedProduct(product);
                } else {
                    setIngredientsList([]);
                    setSelectedProduct(product);
                }
            } else {
                setIngredientsList([]);
                setSelectedProduct(null);
            }
        } catch (error) {
            console.error("Error parsing ingredients:", error);
            setIngredientsList([]);
            setSelectedProduct(null);
        }
    };

    const getPackagingCardStyle = () => {
        if (!selectedPackagingId) return {};
        const pMat = rawMaterialsList.find(rm => String(rm.id) === String(selectedPackagingId));
        const unit = unitMap[String(pMat?.unit_id)] || pMat?.unit_id || "pcs";
        if (unit.toLowerCase() === "pcs") {
            return { backgroundColor: "#d1e7dd" }; // beautiful light green
        } else if (unit.toLowerCase() === "kg") {
            return { backgroundColor: "#f8d7da" }; // beautiful light red
        }
        return {};
    };

    const fetchRawMaterialsForProcess = async (processingIds) => {
        if (!processingIds || (Array.isArray(processingIds) && processingIds.length === 0)) {
            setRawMaterialsListForProcess([]);
            return;
        }
        try {
            showLoader();
            const ids = Array.isArray(processingIds) ? processingIds : [processingIds];
            let allTransactions = [];
            const timestamp = new Date().getTime();
            // const response = await list_raw_material_transactions(processingId, timestamp);

            for (const pid of ids) {
                if (!pid) continue;
                const response = await list_raw_material_transactions(pid, timestamp);
                if (response.data && response.data.transactions) {
                    allTransactions = [...allTransactions, ...response.data.transactions];
                }
            }

            // Combine duplicate raw material transactions in the list
            const combinedTx = {};
            allTransactions.forEach(tx => {
                const key = tx.raw_mat_id;
                if (!combinedTx[key]) {
                    combinedTx[key] = { ...tx, qty: 0, price: 0 };
                }
                combinedTx[key].qty += parseFloat(tx.qty || 0);
                combinedTx[key].price += parseFloat(tx.price || 0);
            });

            setRawMaterialsListForProcess(Object.values(combinedTx));
        } catch (error) {
            console.error("Error fetching raw materials for process:", error);
            setRawMaterialsListForProcess([]);
        } finally {
            hideLoader();
        }
    };

    const validateProductDetails = () => {
        const temp = {};
        if (!formData1.p_id) temp.p_id = "Product is required";
        if (!formData1.p_weight) temp.p_weight = "Weight is required";
        if (!formData1.pbox_unit) temp.pbox_unit = "Unit is required";
        if (!formData1.p_price) temp.p_price = "Price is required";

        // Check for duplicate weight and unit in the same product
        const targetPId = formData1.p_id?.toString();
        const targetWeight = parseFloat(formData1.p_weight);
        const targetUnit = formData1.pbox_unit?.trim().toLowerCase();

        if (targetPId && !isNaN(targetWeight) && targetUnit) {
            const isDuplicate = distributionItems.some(detail => {
                // Skip check for "FREE SIZE / LOSE QTY" default details
                if (detail.pbox_code === "FREE SIZE / LOSE QTY" || detail.id === "FREE_SIZE") return false;

                const detWeight = parseFloat(detail.p_weight);
                const detUnit = detail.pbox_unit?.trim().toLowerCase();

                return (
                    !isNaN(detWeight) &&
                    Math.abs(detWeight - targetWeight) < 0.0001 &&
                    detUnit === targetUnit
                );
            });

            if (isDuplicate) {
                temp.p_weight = "This product weight already exists.";
                temp.pbox_unit = "This product weight already exists.";
                toast.error(`Product weight pack '${formData1.p_weight} ${formData1.pbox_unit}' already exists for this product.`);
            }
        }

        setErrorss(temp);
        return Object.keys(temp).length === 0;
    };

    const resetProductDetailsForm = () => {
        setFormData1({
            p_id: "",
            p_code: "",
            p_weight: "",
            pbox_unit: "",
            p_price: "",
            pbox_qty: "",
            pbox_code: "",
            pbox_price: ""
        });
        setErrorss({});
    };

    const handleProductDetailsSubmit = async (e) => {
        e.preventDefault();
        if (!validateProductDetails()) return;

        setLoading(true);
        try {
            const response = await create_product_details(formData1);
            if (response.data && response.data.status === "success") {
                toast.success("Product details added successfully");
                resetProductDetailsForm();
                setShowDetailsModal(false);
                if (selectedBatch) {
                    await fetchProductDetails(selectedBatch.p_id);
                }
            } else {
                toast.error(response.data?.message || "Failed to add product details");
            }
        } catch (error) {
            console.error("Error adding product details:", error);
            toast.error(error?.response?.data?.message || "Error adding product details");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDistributeModal = async (prod) => {
        const combinedBatch = {
            id: prod.batches[0]?.id || "",
            p_id: prod.p_id,
            product_title: prod.product_title,
            qty: prod.total_qty,
            remaining: prod.total_remaining,
            units: prod.units,
            mfg_date: getMfgDateDisplay(prod.mfg_dates),
            expiry_date: getExpiryDateDisplay(prod.expiry_dates),
            batch_no: prod.batches.map(b => b.batch_no).join(", "),
            mfg_cost_overall: prod.batches.reduce((sum, b) => sum + parseFloat(b.mfg_cost_overall || 0), 0),
            mfg_price: prod.batches[0]?.mfg_price || "0",
            sales_price_unit: prod.batches[0]?.sales_price_unit || "0",
            batches: prod.batches
        };

        setSelectedBatch(combinedBatch);
        fetchBankBalance();
        fetchProductDetails(prod.p_id, combinedBatch);
        fetchRawMaterials();

        const processingIds = prod.batches.map(b => b.processing_id).filter(Boolean);
        fetchRawMaterialsForProcess(processingIds);

        // Reset Modal Form
        setSelectedPackagingId("");
        setSelectedPackagingQty("");
        setSelectedPackagingPrice("");
        setExpenseItems([
            { id: 1, description: 'Labour Charges', amount: 0 },
            { id: 2, description: 'Package Charges', amount: 0 },
            { id: 3, description: 'EB/Rent Charges', amount: 0 },
            { id: 4, description: 'Other Charges', amount: 0 }
        ]);
        setCustomExpenses([]);
        setDistributionItems([]);

        const kgId = unitsList.find(u => u.title?.toUpperCase() === 'KG' || u.title?.toLowerCase() === 'kg')?.id || "1";
        const pcsId = unitsList.find(u => u.title?.toLowerCase() === 'pcs')?.id || "pcs";
        setNewDistributionRows([
            {
                id: 1,
                product_qty: "",
                product_unit: kgId,
                packaging_id: "",
                pack_qty: "",
                pack_unit: pcsId,
                wastage_val: "",
                wastage_unit: pcsId,
                pack_qty_edited: false
            }
        ]);

        setModalStep(3); // default to Step 3
        setShowDistributeModal(true);

        try {
            let combinedProcessDetails = {
                labour_charges: 0,
                package_charges: 0,
                eb_rent_charges: 0,
                other_charges: 0,
                mfg_price_unit: parseFloat(prod.batches[0]?.mfg_price || 0),
                sales_price_unit: parseFloat(prod.batches[0]?.sales_price_unit || 0)
            };

            for (const b of prod.batches) {
                const res = await get_product_processing({ batch_no: b.batch_no });
                if (res.data && res.data.status === "success" && res.data.data && res.data.data.length > 0) {
                    const pd = res.data.data[0];
                    combinedProcessDetails.labour_charges += parseFloat(pd.labour_charges || 0);
                    combinedProcessDetails.package_charges += parseFloat(pd.package_charges || 0);
                    combinedProcessDetails.eb_rent_charges += parseFloat(pd.eb_rent_charges || 0);
                    combinedProcessDetails.other_charges += parseFloat(pd.other_charges || 0);
                }
            }
            setProcessDetails(combinedProcessDetails);
        } catch (error) {
            console.error("Error fetching process details:", error);
            setProcessDetails(null);
        }
    };

    const handleOpenTransferModal = (prodOrBatch) => {
        if (!prodOrBatch) return;

        let batchItem = null;
        let parentProd = null;

        if (prodOrBatch.batch_no && !prodOrBatch.batches) {
            batchItem = prodOrBatch;
            parentProd = {
                p_id: prodOrBatch.p_id,
                product_title: prodOrBatch.product_title || `Product #${prodOrBatch.p_id}`,
                batches: [prodOrBatch]
            };
        } else if (prodOrBatch.batches && prodOrBatch.batches.length > 0) {
            parentProd = prodOrBatch;
            batchItem = prodOrBatch.batches[0];
        }

        if (!batchItem) {
            toast.error("No batch found to transfer.");
            return;
        }

        setTransferSourceProduct(parentProd);
        setTransferSourceBatch(batchItem);
        setTransferTargetProduct(null);
        setTransferType("partial");
        setTransferQty("");
        setTransferDate(getFormattedCurrentDate2());
        setTransferRemarks(`Transferred from ${parentProd.product_title || 'Product'} (Batch ${batchItem.batch_no})`);
        setTransferError("");
        setShowTransferModal(true);
    };

    const handleTransferSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (isTransferring) return;

        if (!transferSourceBatch) {
            toast.error("Source batch not found.");
            return;
        }

        if (!transferTargetProduct || !transferTargetProduct.value) {
            toast.error("Please select a destination end product.");
            return;
        }

        if (!transferDate) {
            toast.error("Please select a valid transfer date.");
            return;
        }

        const sourcePId = transferSourceBatch.p_id || transferSourceProduct?.p_id;
        if (String(transferTargetProduct.value) === String(sourcePId)) {
            toast.error("Destination product cannot be the same as source product.");
            return;
        }

        const avl = parseFloat(transferSourceBatch.remaining || 0);
        const qtyNum = parseFloat(transferQty);
        if (isNaN(qtyNum) || qtyNum <= 0) {
            toast.error("Please enter a valid quantity greater than 0.");
            return;
        }

        if (qtyNum > avl + 0.0001) {
            toast.error(`Transfer quantity (${qtyNum.toFixed(2)} kg) exceeds available stock (${avl.toFixed(2)} kg).`);
            return;
        }

        setIsTransferring(true);
        showLoader();
        try {
            const payload = {
                source_processing_id: transferSourceBatch.processing_id || null,
                source_batch_no: transferSourceBatch.batch_no,
                source_p_id: sourcePId,
                target_p_id: transferTargetProduct.value,
                transfer_qty: qtyNum,
                transfer_type: transferType,
                transfer_date: transferDate || getFormattedCurrentDate2(),
                date: transferDate || getFormattedCurrentDate2(),
                remarks: transferRemarks,
                user_id: UserItem?.user_id || "1",
                shop_id: shopItem?.shop_id || shopItem?.id || transferSourceBatch.shop_id || "0",
                branch_id: localStorage.getItem('branch_id') || transferSourceBatch.branch_id || "1",
                warehouse_id: warehouse?.id || transferSourceBatch.warehouse_id || "1"
            };

            const response = await transfer_end_product_quantity(payload);
            if (response.data && response.data.status === "success") {
                toast.success(response.data.message || "Product quantity successfully transferred under the same batch!");
                setShowTransferModal(false);
                setTransferSourceBatch(null);
                setTransferSourceProduct(null);
                setTransferTargetProduct(null);
                setTransferQty("");
                fetchUndistributedProducts();
                fetchRawMaterials();
            } else {
                toast.error(response.data?.message || "Failed to transfer quantity.");
            }
        } catch (error) {
            console.error("Error in transfer_end_product_quantity:", error);
            const errMsg = error.response?.data?.message || "An error occurred while transferring quantity.";
            setTransferError(errMsg);
            toast.error(errMsg);
        } finally {
            setIsTransferring(false);
            hideLoader();
        }
    };

    const renderStep1Content = () => {
        if (!selectedBatch) return null;
        return (
            <div className="text-start">
                <div className="row g-3">
                    {/* Product Selection */}
                    <div className="col-md-12">
                        <label className="form-label fw-bold">Selected Product</label>
                        <select className="form-select bg-light" value={selectedBatch.p_id} disabled>
                            <option value={selectedBatch.p_id}>{selectedBatch.product_title}</option>
                        </select>
                    </div>

                    {/* Desired Quantity */}
                    <div className="col-md-4">
                        <label className="form-label fw-bold">Desired Quantity</label>
                        <input
                            type="text"
                            className="form-control bg-light"
                            value={Number(selectedBatch.qty).toFixed(2)}
                            disabled
                        />
                    </div>

                    {/* Unit */}
                    <div className="col-md-4">
                        <label className="form-label fw-bold">Unit</label>
                        <select className="form-select bg-light" value={selectedBatch.units || "kg"} disabled>
                            <option value={selectedBatch.units || "kg"}>{selectedBatch.units === "kg" ? "Kilograms" : selectedBatch.units || "kg"}</option>
                        </select>
                    </div>

                    {/* Manufacturing Date */}
                    <div className="col-md-4">
                        <label className="form-label fw-bold">Manufacturing Date</label>
                        <input
                            type="text"
                            className="form-control bg-light"
                            value={selectedBatch.mfg_date ? formatDateDisplay(selectedBatch.mfg_date) : ""}
                            disabled
                        />
                    </div>
                </div>

                {/* Ingredients Section */}
                {selectedProduct && ingredientsList.length > 0 && (
                    <div className="col-12 mt-4 text-start">
                        <div className="card border">
                            <div className="card-header bg-light">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0 fw-bold">
                                        <i className="bi bi-list-ul me-2"></i>
                                        Required Ingredients for {selectedProduct.title} - Showing data for 1KG
                                    </h6>
                                    <span className="badge bg-primary">
                                        {ingredientsList.length} ingredient(s)
                                    </span>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-bordered table-striped table-sm mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '10%' }}>S.No</th>
                                                <th style={{ width: '50%' }}>Ingredient Name</th>
                                                <th style={{ width: '20%' }}>Quantity</th>
                                                <th style={{ width: '20%' }}>Unit</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ingredientsList.map((ing, index) => (
                                                <tr key={index}>
                                                    <td className="text-center">{index + 1}</td>
                                                    <td>
                                                        <strong>{ing.title}</strong>
                                                        <small className="text-muted ms-1">
                                                            (avbl: {(Number(ing.remaining_stock) || 0).toFixed(2)})
                                                        </small>
                                                    </td>
                                                    <td>{ing.custom_qty}</td>
                                                    <td>{ing.custom_unit}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Navigation Buttons */}
                <div className="col-12 d-flex justify-content-between mt-4">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowDistributeModal(false)}
                    >
                        <i className="bi bi-x-circle me-1"></i>Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setModalStep(2)}
                    >
                        Next: Manufacturing <i className="bi bi-arrow-right ms-1"></i>
                    </button>
                </div>
            </div>
        );
    };

    const renderStep2Content = () => {
        if (!selectedBatch) return null;

        // Find the packaging material transaction inside rawMaterialsListForProcess if any
        const bulkPackagingMat = rawMaterialsListForProcess.find(item => {
            const rm = rawMaterialsList.find(r => String(r.id) === String(item.raw_mat_id));
            return rm && String(rm.type) === "1";
        });

        // Filter out the packaging material transaction from raw materials used table
        const bulkRawMaterialsOnly = rawMaterialsListForProcess.filter(item => {
            const rm = rawMaterialsList.find(r => String(r.id) === String(item.raw_mat_id));
            return !rm || String(rm.type) !== "1";
        });

        const totalBulkRawMaterialCost = bulkRawMaterialsOnly.reduce((sum, item) => sum + parseFloat(item.price || 0), 0);
        const totalBulkPackagingCost = bulkPackagingMat ? parseFloat(bulkPackagingMat.price || 0) : 0;
        const totalBulkExpenses = parseFloat(processDetails?.labour_charges || 0) +
            parseFloat(processDetails?.package_charges || 0) +
            parseFloat(processDetails?.eb_rent_charges || 0) +
            parseFloat(processDetails?.other_charges || 0);
        const totalBulkCost = parseFloat(selectedBatch.mfg_cost_overall || (totalBulkRawMaterialCost + totalBulkPackagingCost + totalBulkExpenses) || 0);

        const mfg_price = parseFloat(processDetails?.mfg_price_unit || selectedBatch?.mfg_price || 0);
        const sales_price = parseFloat(processDetails?.sales_price_unit || selectedBatch?.sales_price_unit || 0);
        const profitPercent = mfg_price > 0 ? Math.round(((sales_price - mfg_price) / mfg_price) * 100) : 0;

        return (
            <div className="text-start">
                {/* Product Info Alert */}
                <div className="alert alert-info">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                            <strong>Product:</strong> {selectedBatch.product_title}
                        </div>
                        <div>
                            <strong>Desired Qty:</strong> {Number(selectedBatch.qty).toFixed(2)} {selectedBatch.units || 'kg'}
                        </div>
                        {selectedBatch.batch_no && (
                            <div className="text-truncate" style={{ maxWidth: '400px' }} title={selectedBatch.batch_no}>
                                <strong>Batch #:</strong> {formatBatchNo(selectedBatch.batches)}
                            </div>
                        )}
                    </div>
                </div>

                {/* Packaging Material Section */}
                <div className="card mb-4" style={{ backgroundColor: bulkPackagingMat ? "#d1e7dd" : "transparent", transition: "background-color 0.3s ease" }}>
                    <div className="card-header bg-light" style={{ backgroundColor: "transparent" }}>
                        <h6 className="mb-0 text-start">Packaging Material</h6>
                    </div>
                    <div className="card-body">
                        <div className="row g-3 text-start">
                            {/* Material Selection */}
                            <div className="col-md-4">
                                <label className="form-label">Select Packaging Material</label>
                                <select
                                    className="form-select bg-light"
                                    value={bulkPackagingMat ? bulkPackagingMat.raw_mat_id : ""}
                                    disabled
                                    readOnly
                                >
                                    {bulkPackagingMat ? (
                                        <option value={bulkPackagingMat.raw_mat_id}>
                                            {bulkPackagingMat.raw_material_name}
                                        </option>
                                    ) : (
                                        <option value="">No Packaging Material Used</option>
                                    )}
                                </select>
                            </div>

                            {/* Quantity */}
                            <div className="col-md-2">
                                <label className="form-label">Quantity</label>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control bg-light"
                                        value={bulkPackagingMat ? parseFloat(bulkPackagingMat.qty || 0).toFixed(2) : "0.00"}
                                        disabled
                                        readOnly
                                    />
                                    <span className="input-group-text bg-light text-muted">
                                        {bulkPackagingMat ? (unitMap[String(bulkPackagingMat.unit_id)] || bulkPackagingMat.unit_id || "pcs") : "pcs"}
                                    </span>
                                </div>
                            </div>

                            {/* Unit Price */}
                            <div className="col-md-3">
                                <label className="form-label">Unit Price (₹)</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="text"
                                        className="form-control bg-light"
                                        value={bulkPackagingMat ? parseFloat(bulkPackagingMat.price / bulkPackagingMat.qty || 0).toFixed(2) : "0.00"}
                                        disabled
                                        readOnly
                                    />
                                </div>
                            </div>

                            {/* Total Price */}
                            <div className="col-md-3">
                                <label className="form-label">Total Price (₹)</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="text"
                                        className="form-control bg-light"
                                        value={bulkPackagingMat ? parseFloat(bulkPackagingMat.price || 0).toFixed(2) : "0.00"}
                                        disabled
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Raw Materials Used Section */}
                <div className="card mb-4">
                    <div className="card-header bg-light">
                        <h6 className="mb-0">Raw Materials Used</h6>
                    </div>
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-bordered table-sm">
                                <thead>
                                    <tr>
                                        <th width="50">No.</th>
                                        <th>Material Name</th>
                                        <th width="120">Quantity</th>
                                        <th width="100">Unit</th>
                                        <th width="120">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bulkRawMaterialsOnly && bulkRawMaterialsOnly.length > 0 ? (
                                        bulkRawMaterialsOnly.map((material, index) => (
                                            <tr key={material.id || index}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <div><strong>{material.raw_material_name}</strong></div>
                                                </td>
                                                <td className="text-end">
                                                    {(() => {
                                                        const u = String(unitMap[material.unit_id] || material.unit_id || "").toLowerCase();
                                                        const q = parseFloat(material.qty || 0);
                                                        return (u === "grams" || u === "g" || u === "gram" || u === "ml" || u === "pcs")
                                                            ? Math.round(q)
                                                            : q.toFixed(3);
                                                    })()}
                                                </td>
                                                <td>
                                                    {unitMap[material.unit_id] || "-"}
                                                </td>
                                                <td className="text-end">₹{parseFloat(material.price || 0).toFixed(2)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center">
                                                No raw materials data available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr className="table-light">
                                        <td colSpan="4" className="text-end"><strong>Total Raw Material Cost:</strong></td>
                                        <td className="text-end">
                                            <strong>₹{totalBulkRawMaterialCost.toFixed(2)}</strong>
                                        </td>
                                    </tr>
                                    <tr className="table-secondary">
                                        <td colSpan="4" className="text-end"><strong>Overall Cost (Raw + Packaging):</strong></td>
                                        <td className="text-end">
                                            <strong>
                                                ₹{(totalBulkRawMaterialCost + totalBulkPackagingCost).toFixed(2)}
                                            </strong>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Outcome Quantity & Expiry Date (Read-Only) */}
                <div className="row mb-3">
                    <div className="col-md-6">
                        <label className="form-label">Outcome Quantity</label>
                        <div className="input-group">
                            <input
                                type="text"
                                className="form-control bg-light"
                                value={Number(selectedBatch.qty).toFixed(2)}
                                disabled
                                readOnly
                            />
                            <span className="input-group-text">{selectedBatch.units || 'kg'}</span>
                        </div>
                        <small className="text-muted">The actual quantity produced</small>
                    </div>

                    <div className="col-md-6">
                        <label className="form-label">Expiry Date</label>
                        <input
                            type="text"
                            className="form-control bg-light"
                            value={selectedBatch.expiry_date || ""}
                            disabled
                            readOnly
                        />
                        <small className="text-muted">When this product will expire</small>
                    </div>
                </div>

                {/* Manufacturing Expenses Section (Read-Only) */}
                <div className="card mb-4">
                    <div className="card-header bg-light">
                        <h6 className="mb-0">Manufacturing Expenses</h6>
                    </div>
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label">Labour Charges</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="text"
                                        value={parseFloat(processDetails?.labour_charges || 0).toFixed(2)}
                                        className="form-control bg-light"
                                        disabled
                                        readOnly
                                    />
                                </div>
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">EB/Rent Charges</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="text"
                                        value={parseFloat(processDetails?.eb_rent_charges || 0).toFixed(2)}
                                        className="form-control bg-light"
                                        disabled
                                        readOnly
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Other Charges</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="text"
                                        value={parseFloat(processDetails?.other_charges || 0).toFixed(2)}
                                        className="form-control bg-light"
                                        disabled
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Cost Summary Card (Read-Only) */}
                <div className="card mb-4 bg-light border">
                    <div className="card-body">
                        <h6 className="card-title fw-bold mb-3">Manufacturing Cost Summary</h6>
                        <hr className="my-2" />
                        <div className="d-flex justify-content-between mb-2">
                            <h6 className="mb-0 fw-bold">Total Manufacturing Cost:</h6>
                            <h6 className="mb-0 text-primary fw-bold">₹{totalBulkCost.toFixed(2)}</h6>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mt-2">
                            <div className="d-flex align-items-center">
                                <span className="me-2">
                                    Suggested Selling Price (
                                    <input
                                        type="number"
                                        value={profitPercent}
                                        className="form-control d-inline-block bg-light text-center"
                                        style={{ width: "70px", display: "inline-block" }}
                                        disabled
                                        readOnly
                                    />
                                    <span className="ms-1">%</span>
                                    ):
                                </span>
                            </div>

                            <strong className="text-success">
                                {profitPercent
                                    ? `${profitPercent}% → ₹${(totalBulkCost * profitPercent / 100).toFixed(2)}`
                                    : "—"}
                            </strong>
                        </div>
                    </div>
                </div>

                {/* Navigation Buttons */}
                <div className="d-flex justify-content-between mt-4">
                    <div>
                        <button
                            type="button"
                            className="btn btn-outline-secondary me-2"
                            onClick={() => setModalStep(1)}
                            disabled={isSubmitting}
                        >
                            <i className="bi bi-arrow-left me-1"></i> Back
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => setShowDistributeModal(false)}
                            disabled={isSubmitting}
                        >
                            <i className="bi bi-x-circle me-1"></i> Cancel Process
                        </button>
                    </div>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setModalStep(3)}
                        disabled={isSubmitting}
                    >
                        Next: Distribution <i className="bi bi-arrow-right ms-1"></i>
                    </button>
                </div>
            </div>
        );
    };

    const renderStep3Content = () => {
        if (!selectedBatch) return null;

        return (
            <div className="text-start">
                {/* Product Info Alert */}
                <div className="alert alert-info py-2 mb-3 text-start">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <span><strong>Product:</strong> {selectedBatch.product_title}</span>
                        <span className="text-truncate" style={{ maxWidth: '400px' }} title={selectedBatch.batch_no}>
                            <strong>Batch:</strong> {formatBatchNo(selectedBatch.batches)}
                        </span>
                        <span>
                            <strong>Total Quantity:</strong>{" "}
                            {(() => {
                                const qtyKg = parseFloat(selectedBatch.remaining || 0);
                                const qtyGrams = qtyKg * 1000;
                                // return `${qtyGrams.toFixed(0)} grams (${qtyKg.toFixed(2)} kg)`;
                                return `${qtyKg.toFixed(3)} kg`;
                            })()}
                        </span>
                    </div>
                </div>

                {/* NEW DISTRIBUTION FLOW SECTION (Multi-Column Vertical Card Layout) */}
                <div className="card mt-3 shadow-sm" style={{ borderColor: "#1e293b", borderRadius: "12px", overflow: "hidden" }}>
                    <div className="card-header text-white d-flex justify-content-between align-items-center py-2.5 px-3" style={{ backgroundColor: "#1e293b" }}>
                        <h6 className="mb-0 text-white fw-bold d-flex align-items-center gap-2">
                            <i className="bi bi-box-seam fs-5"></i>Product Package Distribution
                        </h6>
                        <button
                            type="button"
                            className="btn btn-light btn-sm fw-semibold shadow-sm"
                            onClick={addNewDistributionRow}
                        >
                            <i className="bi bi-plus-circle me-1"></i>Add
                        </button>
                    </div>

                    <div className="card-body p-3 bg-light">
                        {/* Vertical Cards for Each Package Entry */}
                        {newDistributionRows.map((row, index) => {
                            const calcs = getNewRowCalculations(row);
                            return (
                                <div key={row.id || index} className="card border shadow-sm mb-3" style={{ borderRadius: "10px", backgroundColor: "#ffffff" }}>
                                    {/* Card Sub-Header */}
                                    <div className="card-header bg-white py-2 px-3 d-flex justify-content-between align-items-center border-bottom">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="badge bg-primary rounded-circle px-2 py-1 fs-6">#{index + 1}</span>
                                            <span className="fw-bold text-dark small">Package Distribution Item #{index + 1}</span>
                                        </div>
                                        {newDistributionRows.length > 1 && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger px-2.5 py-1 rounded-pill d-flex align-items-center gap-1"
                                                title="Remove Row"
                                                onClick={() => removeNewDistributionRow(index)}
                                            >
                                                <i className="bi bi-trash"></i> <span className="small">Delete Row</span>
                                            </button>
                                        )}
                                    </div>

                                    {/* Card Content - 3 Responsive Columns */}
                                    <div className="card-body p-3">
                                        <div className="row g-3">

                                            {/* Section 1: Product Powder Allocation */}
                                            <div className="col-md-4">
                                                <div className="p-3 rounded-3 border h-100" style={{ backgroundColor: "#f0f9ff", borderColor: "#bae6fd" }}>
                                                    <div className="fw-bold text-primary mb-2.5 small d-flex align-items-center gap-1.5 border-bottom pb-1.5" style={{ borderColor: "#bae6fd" }}>
                                                        <i className="bi bi-box"></i> 1. Powder Allocation
                                                    </div>
                                                    <div className="row g-2">
                                                        <div className="col-12">
                                                            <label className="form-label text-muted mb-1 small">Product Qty Used</label>
                                                            <div className="input-group input-group-sm">
                                                                <input
                                                                    type="number"
                                                                    className="form-control text-center fw-semibold"
                                                                    placeholder="Qty"
                                                                    value={row.product_qty}
                                                                    onChange={(e) => handleNewRowChange(index, "product_qty", e.target.value)}
                                                                    onWheel={(e) => e.target.blur()}
                                                                    step="any" min="0"
                                                                />
                                                                <select
                                                                    className="form-select px-1"
                                                                    style={{ maxWidth: "85px" }}
                                                                    value={row.product_unit || ""}
                                                                    onChange={(e) => handleNewRowChange(index, "product_unit", e.target.value)}
                                                                >
                                                                    <option value="">Unit</option>
                                                                    {getAvailableUnits.map((unit) => (
                                                                        <option key={unit.id} value={unit.id}>{unit.title}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                        <div className="col-6 mt-2">
                                                            <span className="text-muted small d-block">Price / kg</span>
                                                            <span className="fw-bold text-dark">₹{calcs.costPerKg.toFixed(2)}</span>
                                                        </div>
                                                        <div className="col-6 text-end mt-2">
                                                            <span className="text-muted small d-block">Bulk Cost</span>
                                                            <span className="fw-bold text-primary">₹{calcs.productBulkCost.toFixed(2)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Section 2: Packaging Material & Wastages */}
                                            <div className="col-md-5">
                                                <div className="p-3 rounded-3 border h-100" style={{ backgroundColor: "#fff7ed", borderColor: "#fed7aa" }}>
                                                    <div className="fw-bold text-warning-emphasis mb-2.5 small d-flex align-items-center gap-1.5 border-bottom pb-1.5" style={{ borderColor: "#fed7aa" }}>
                                                        <i className="bi bi-bag"></i> 2. Packaging & Wastages
                                                    </div>
                                                    <div className="row g-2">
                                                        <div className="col-12">
                                                            <label className="form-label text-muted mb-1 small">Pack Material</label>
                                                            <Select
                                                                options={packMaterialOptions}
                                                                value={packMaterialOptions.find(opt => String(opt.value) === String(row.packaging_id)) || null}
                                                                onChange={(selected) => handleNewRowChange(index, "packaging_id", selected ? selected.value : "")}
                                                                placeholder="-- Select Material --"
                                                                isSearchable={true}
                                                                isClearable={true}
                                                                isOptionDisabled={(option) => Boolean(option.isDisabled)}
                                                                styles={selectMaterialStyles}
                                                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                                            />
                                                        </div>
                                                        <div className="col-6">
                                                            <label className="form-label text-muted mb-1 small">Pack Qty</label>
                                                            <div className="input-group input-group-sm">
                                                                <input
                                                                    type="number"
                                                                    className="form-control text-center"
                                                                    placeholder="Qty"
                                                                    value={row.pack_qty}
                                                                    onChange={(e) => handleNewRowChange(index, "pack_qty", e.target.value)}
                                                                    onWheel={(e) => e.target.blur()}
                                                                    step="any" min="0"
                                                                />
                                                                <select
                                                                    className="form-select px-1"
                                                                    style={{ maxWidth: "80px" }}
                                                                    value={row.pack_unit || "pcs"}
                                                                    onChange={(e) => handleNewRowChange(index, "pack_unit", e.target.value)}
                                                                >
                                                                    <option value="">Unit</option>
                                                                    {getAvailableUnits
                                                                        .filter((unit) => {
                                                                            const title = (unit.title || "").toString().trim().toLowerCase();
                                                                            return title === "kg" || title === "pcs";
                                                                        })
                                                                        .map((unit) => (
                                                                            <option key={unit.id} value={unit.id}>{unit.title}</option>
                                                                        ))}
                                                                </select>
                                                            </div>
                                                            {calcs.isKgPackUnit && calcs.pcsPerKg > 0 && (
                                                                <div className="text-muted mt-1 font-monospace fw-semibold" style={{ fontSize: '0.70rem' }}>
                                                                    (1 Kg = {calcs.pcsPerKg} pcs)
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="col-6">
                                                            <label className="form-label text-muted mb-1 small">Wastages</label>
                                                            <div className="input-group input-group-sm">
                                                                <input
                                                                    type="number"
                                                                    className="form-control text-center"
                                                                    placeholder="0"
                                                                    value={row.wastage_val}
                                                                    onChange={(e) => handleNewRowChange(index, "wastage_val", e.target.value)}
                                                                    onWheel={(e) => e.target.blur()}
                                                                    step="any" min="0"
                                                                />
                                                                <select
                                                                    className="form-select px-1"
                                                                    style={{ maxWidth: "80px" }}
                                                                    value={row.wastage_unit || "pcs"}
                                                                    onChange={(e) => handleNewRowChange(index, "wastage_unit", e.target.value)}
                                                                >
                                                                    <option value="pcs">pcs</option>
                                                                    {getAvailableUnits.map((unit) => (
                                                                        <option key={unit.id} value={unit.id}>{unit.title}</option>
                                                                    ))}
                                                                    <option value="₹">₹</option>
                                                                </select>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Section 3: Costing & Output */}
                                            <div className="col-md-3">
                                                <div className="p-3 rounded-3 border h-100 d-flex flex-column justify-content-between" style={{ backgroundColor: "#ecfdf5", borderColor: "#a7f3d0" }}>
                                                    <div className="fw-bold text-success mb-2.5 small d-flex align-items-center gap-1.5 border-bottom pb-1.5" style={{ borderColor: "#a7f3d0" }}>
                                                        <i className="bi bi-calculator"></i> 3. Costing & Output
                                                    </div>
                                                    <div>
                                                        <div className="d-flex justify-content-between mb-1.5">
                                                            <span className="text-muted small">Bulk Cost:</span>
                                                            <span className="fw-semibold text-dark">₹{calcs.productBulkCost.toFixed(2)}</span>
                                                        </div>
                                                        <div className="d-flex justify-content-between mb-1.5">
                                                            <span className="text-muted small">Pack Cover Price:</span>
                                                            <span className="fw-semibold text-dark">₹{calcs.packCoverPrice.toFixed(2)}</span>
                                                        </div>
                                                        <div className="d-flex justify-content-between mb-1.5">
                                                            <span className="text-muted small">Overall Costing:</span>
                                                            <span className="fw-bold text-primary">₹{calcs.overallCosting.toFixed(2)}</span>
                                                        </div>
                                                    </div>
                                                    <div className="mt-2 text-center pt-2 border-top border-success-subtle">
                                                        <span className="text-muted small d-block mb-1">Per Piece Costing</span>
                                                        <span className="badge bg-success fs-6 fw-semibold px-3 py-1.5 shadow-sm">
                                                            ₹{parseFloat(calcs.perPieceCosting.toFixed(3))} / pc
                                                        </span>
                                                        {calcs.usablePcs > 0 && (
                                                            <div className="text-muted extra-small mt-1">({calcs.usablePcs.toFixed(0)} usable pcs)</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {/* Overall Batch Summary Banner */}
                        {(() => {
                            const summary = calculateNewDistributionSummary();
                            return (
                                <div className="card border-0 shadow-sm rounded-3 mt-3" style={{ backgroundColor: "#1e293b", color: "#ffffff" }}>
                                    <div className="card-body p-3">
                                        <div className="row g-3 align-items-center text-center text-md-start">
                                            <div className="col-md-3">
                                                <span className="text-white-50 d-block small">Total Distributed</span>
                                                <span className={`fw-bold fs-6 ${summary.totalDistributedKg > summary.totalBulkKg ? "text-danger" : "text-success"}`}>
                                                    {summary.totalDistributedKg.toFixed(3)} kg
                                                </span>
                                            </div>
                                            <div className="col-md-3">
                                                <span className="text-white-50 d-block small">Remaining Bulk Stock</span>
                                                <span className={`fw-bold fs-6 ${summary.remainingKg < 0 ? "text-danger" : "text-warning"}`}>
                                                    {summary.remainingKg.toFixed(3)} kg
                                                </span>
                                            </div>
                                            <div className="col-md-3">
                                                <span className="text-white-50 d-block small">Packaging & Wastage Cost</span>
                                                <span className="fw-bold fs-6 text-info">
                                                    ₹{(summary.totalPackCost + summary.totalWastageCost).toFixed(2)}
                                                </span>
                                            </div>
                                            <div className="col-md-3 text-md-end">
                                                <span className="text-white-50 d-block small">Total Process Cost</span>
                                                <span className="fw-bold fs-5 text-success">₹{summary.totalOverallCosting.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                    </div>
                </div>

                {/* PREVIOUS PACKAGE DISTRIBUTION TABLE STRUCTURE (COMMENTED OUT AS REQUESTED) */}
                {/*
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0 fw-bold">Package Distribution</h6>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => {
                            resetProductDetailsForm();
                            setFormData1({
                                p_id: selectedBatch.p_id,
                                p_code: "",
                                p_weight: "",
                                pbox_unit: "",
                                p_price: "",
                                pbox_qty: "",
                                pbox_code: "",
                                pbox_price: ""
                            });
                            setShowDetailsModal(true);
                        }}
                    >
                        <i className="bi bi-plus-circle me-1"></i>Add Package Size
                    </button>
                </div>
                <div className="card border shadow-none mb-3">
                    <div className="card-body p-0">
                        {distributionItems.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-bordered table-sm mb-0" style={{ width: "100%", fontFamily: "poppins" }}>
                                    <thead className="table-light">
                                        <tr>
                                            <th width="40">No.</th>
                                            <th>Package Type</th>
                                            <th width="120">Weight</th>
                                            <th width="150">Quantity (Packets)</th>
                                            <th width="150">Total Weight</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {distributionItems.map((item, index) => (
                                            <tr key={item.id}>
                                                <td className="text-center">{index + 1}</td>
                                                <td className="text-start">{item.product_title}</td>
                                                <td className="text-center">{item.p_weight + " " + item.pbox_unit}</td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm text-center"
                                                        value={item.qty || ""}
                                                        onChange={(e) => handleDistributionQtyChange(item.id, e.target.value)}
                                                        onWheel={(e) => e.currentTarget.blur()}
                                                        min="0"
                                                        step="any"
                                                    />
                                                </td>
                                                <td className="text-center">
                                                    <strong>
                                                        {(() => {
                                                            const totalGrams = convertToGrams(item.p_weight, item.pbox_unit) * (item.qty || 0);
                                                            if (totalGrams <= 999) {
                                                                return `${totalGrams} grams`;
                                                            } else {
                                                                return `${(totalGrams / 1000).toFixed(2)} kg`;
                                                            }
                                                        })()}
                                                    </strong>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="p-3 text-muted text-center small">
                                No package sizes defined for this product master.
                            </div>
                        )}
                    </div>
                </div>
                */}

                {/* Distribution Expenses Section */}
                <div className="card mb-4">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 fw-bold">Distribution Expenses / Charges</h6>
                        <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-secondary text-white small">Bank Balance: ₹{availableBankBalance.toFixed(2)}</span>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                    const newId = customExpenses.length > 0
                                        ? Math.max(...customExpenses.map(item => item.id)) + 1
                                        : 1;

                                    setCustomExpenses([
                                        ...customExpenses,
                                        { id: newId, description: '', amount: 0 }
                                    ]);
                                }}
                            >
                                <i className="bi bi-plus-circle me-1"></i> Add Custom Charge
                            </button>
                        </div>
                    </div>

                    <div className="card-body">
                        <div className="row g-3">
                            {/* Standard expense items */}
                            {expenseItems.map((item) => {
                                if (item.description !== 'Other Charges') {
                                    return null;
                                }
                                return (
                                    <div className="col-md-12" key={item.id}>
                                        <label className="form-label">{item.description}</label>
                                        <div className="input-group">
                                            <span className="input-group-text">₹</span>
                                            <input
                                                type="number"
                                                value={item.amount}
                                                onChange={(e) => handleExpenseChange(item.id, e.target.value)}
                                                onWheel={(e) => e.currentTarget.blur()}
                                                className="form-control"
                                                placeholder="0.00"
                                                min="0"
                                                step="1"
                                            />
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Custom expense items with delete option */}
                            {customExpenses.map((item) => (
                                <div className="col-md-6" key={`custom-${item.id}`}>
                                    <div className="mb-2">
                                        <input
                                            type="text"
                                            value={item.description}
                                            onChange={(e) => {
                                                const updatedItems = customExpenses.map(i =>
                                                    i.id === item.id ? { ...i, description: e.target.value } : i
                                                );
                                                setCustomExpenses(updatedItems);
                                            }}
                                            className="form-control mb-2"
                                            placeholder="Enter expense title"
                                        />
                                    </div>
                                    <div className="input-group">
                                        <span className="input-group-text">₹</span>
                                        <input
                                            type="number"
                                            value={item.amount}
                                            onChange={(e) => {
                                                const updatedItems = customExpenses.map(i =>
                                                    i.id === item.id ? { ...i, amount: e.target.value } : i
                                                );
                                                setCustomExpenses(updatedItems);
                                            }}
                                            onWheel={(e) => e.currentTarget.blur()}
                                            className="form-control"
                                            placeholder="0.00"
                                            min="0"
                                            step="1"
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger"
                                            onClick={() => {
                                                setCustomExpenses(customExpenses.filter(i => i.id !== item.id));
                                            }}
                                        >
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Distribution Cost Summary */}
                <div className="card mb-4 bg-light border">
                    <div className="card-body">
                        <h6 className="card-title fw-bold mb-3">Distribution Cost Summary</h6>
                        <div className="d-flex justify-content-between mb-2">
                            <span>Original Bulk Stock Cost:</span>
                            <strong>₹{Number(selectedBatch.mfg_cost_overall || 0).toFixed(2)}</strong>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                            <span>Extra Cost (Distribution Packaging & Expenses):</span>
                            <strong>₹{totalDistributionCost.toFixed(2)}</strong>
                        </div>
                        <hr className="my-2" />
                        <div className="d-flex justify-content-between mb-0">
                            <h6 className="mb-0 fw-bold">Final Distributed Cost:</h6>
                            <h6 className="mb-0 text-primary fw-bold">
                                ₹{(Number(selectedBatch.mfg_cost_overall || 0) + totalDistributionCost).toFixed(2)}
                            </h6>
                        </div>
                    </div>
                </div>

                {/* Navigation Buttons */}
                <div className="d-flex justify-content-between mt-4">
                    <div>
                        <button
                            type="button"
                            className="btn btn-outline-secondary me-2"
                            onClick={() => setModalStep(2)}
                            disabled={isSubmitting}
                        >
                            <i className="bi bi-arrow-left me-1"></i> Back
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => setShowDistributeModal(false)}
                            disabled={isSubmitting}
                        >
                            <i className="bi bi-x-circle me-1"></i> Cancel Process
                        </button>
                    </div>
                    <button
                        type="submit"
                        className="btn btn-warning text-dark px-4 fw-bold shadow-sm"
                        disabled={!isDistributionComplete() || isSubmitting}
                    >
                        {isSubmitting ? "Distributing..." : "Finalize Distribution"}
                    </button>
                </div>
            </div>
        );
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const getAvailableWeightOptions = () => {
        const detailsToUse = availableProductDetails || [];
        const hasFreeSize = detailsToUse.some(d => d.pbox_code === "FREE SIZE / LOSE QTY" || d.id === "FREE_SIZE");
        if (!hasFreeSize && detailsToUse.length > 0) {
            return [
                ...detailsToUse,
                {
                    id: "FREE_SIZE",
                    pbox_code: "FREE SIZE / LOSE QTY",
                    p_weight: 1,
                    pbox_unit: "kilograms",
                    product_title: "Lose Packs"
                }
            ];
        }
        return detailsToUse;
    };

    const handleUndistributedRowWeightChange = (rowIdOrIndex, selectedDetailId) => {
        const options = getAvailableWeightOptions();
        const selectedDetail = options.find(d => String(d.id) === String(selectedDetailId));
        if (!selectedDetail) return;

        const rawWeight = parseFloat(selectedDetail.p_weight || 0);

        setDistributionItems(prev => {
            return prev.map((item, idx) => {
                if (item.row_id === rowIdOrIndex || idx === rowIdOrIndex || item.id === rowIdOrIndex) {
                    return {
                        ...item,
                        id: selectedDetail.id,
                        pbox_unit: selectedDetail.pbox_unit,
                        product_title: selectedDetail.product_title || `${rawWeight} ${selectedDetail.pbox_unit || 'g'} Package`,
                        p_weight: rawWeight,
                        pbox_code: selectedDetail.pbox_code,
                    };
                }
                return item;
            });
        });
    };

    const handleSubmitDistribution = async (e) => {
        e.preventDefault();

        if (isSubmitting) return;

        if (!selectedBatch) {
            toast.error("Missing batch data.");
            return;
        }

        const summary = calculateNewDistributionSummary();
        if (summary.totalDistributedKg <= 0) {
            toast.error("Please enter product quantity in at least one distribution item.");
            return;
        }

        if (summary.remainingKg < -0.01) {
            toast.error(`Total distributed quantity (${summary.totalDistributedKg.toFixed(2)} kg) exceeds available bulk stock (${summary.totalBulkKg.toFixed(2)} kg).`);
            return;
        }

        // Validate packaging material stock
        for (const row of newDistributionRows) {
            if (row.packaging_id) {
                const calcs = getNewRowCalculations(row);
                const packQtyNum = parseFloat(row.pack_qty || 0);
                if (packQtyNum <= 0) {
                    toast.error("Please enter a valid pack quantity for selected packaging material.");
                    return;
                }
                const rawMat = rawMaterialsList.find(rm => String(rm.id) === String(row.packaging_id));
                if (rawMat) {
                    const matUnitTitle = (unitMap[String(rawMat.unit_id)] || rawMat.unit_id || "kg").toString().trim().toLowerCase();
                    const isKgMat = matUnitTitle === "kg" || matUnitTitle === "1" || matUnitTitle === "kilogram";
                    const pcsPerKg = parseFloat(row.pcs_per_kg || rawMat.pcs_per_kg || 0);
                    const availableStock = parseFloat(rawMat.remaining_stock || 0);

                    let requiredStockInMatUnit = packQtyNum;

                    if (isKgMat) {
                        if (!calcs.isKgPackUnit && pcsPerKg > 0) {
                            // User entered in PCS, material stock is in KG
                            requiredStockInMatUnit = packQtyNum / pcsPerKg;
                        }
                    } else {
                        if (calcs.isKgPackUnit && pcsPerKg > 0) {
                            // User entered in KG, material stock is in PCS
                            requiredStockInMatUnit = packQtyNum * pcsPerKg;
                        }
                    }

                    if (requiredStockInMatUnit > availableStock) {
                        const requiredFormatted = isKgMat ? `${requiredStockInMatUnit.toFixed(3)} KG (${calcs.calculatedPcs} pcs)` : `${requiredStockInMatUnit} pcs`;
                        const availableFormatted = isKgMat ? `${availableStock.toFixed(3)} KG` : `${availableStock} pcs`;
                        toast.error(`Insufficient stock for ${rawMat.title}! Required: ${requiredFormatted}, Available: ${availableFormatted}`);
                        return;
                    }
                }
            }
        }

        // Sum all standard and custom charges to check against bank balance (only if charges entered)
        const standardChargesTotal = parseFloat(otherChargesVal || 0) +
            parseFloat(packageCharges || 0) +
            parseFloat(ebRentCharges || 0) +
            parseFloat(labourCharges || 0);
        const customExpensesTotalVal = customExpenses.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
        const totalExtraExpenses = standardChargesTotal + customExpensesTotalVal;

        if (totalExtraExpenses > 0) {
            let currentBankBalance = availableBankBalance;
            try {
                const shopId = shopItem?.shop_id || shopItem?.id || "";
                const balanceRes = await getBankBalance(shopId);
                if (balanceRes?.data && balanceRes.data.bank_balance !== undefined) {
                    currentBankBalance = Number(balanceRes.data.bank_balance || 0);
                    setAvailableBankBalance(currentBankBalance);
                }
            } catch (err) {
                console.error("Error fetching fresh bank balance:", err);
            }

            if (totalExtraExpenses > currentBankBalance) {
                toast.error(`Insufficient bank balance for extra expenses! Available: ₹${currentBankBalance.toFixed(2)}, Required: ₹${totalExtraExpenses.toFixed(2)}`);
                return;
            }
        }

        // Map newDistributionRows to items with matched product detail id and packet weight
        const distributedAllocationItems = [];
        const packagingMaterialsPayload = [];

        newDistributionRows.forEach(row => {
            const calcs = getNewRowCalculations(row);
            if (calcs.qtyInKg <= 0) return;

            if (row.packaging_id) {
                const mat = rawMaterialsList.find(rm => String(rm.id) === String(row.packaging_id));
                const matUnitId = (unitMap[String(row.pack_unit)] || row.pack_unit || unitMap[String(mat?.unit_id)] || "pcs").toString().toLowerCase();
                const packQtyNum = parseFloat(row.pack_qty || 0);

                if (packQtyNum > 0) {
                    packagingMaterialsPayload.push({
                        raw_mat_id: row.packaging_id,
                        qty: packQtyNum.toString(),
                        price_unit: calcs.costPerPiece.toFixed(2),
                        total_price: calcs.packCoverTotalCost.toFixed(2),
                        unit: matUnitId
                    });
                }

                // Match PMD entry for this cover size from availableProductDetails
                const searchList = availableProductDetails || [];
                let matchedItem = null;
                if (mat && mat.title) {
                    const coverGrams = extractPackagingWeightInGrams(mat.title);
                    if (coverGrams && coverGrams > 0) {
                        matchedItem = searchList.find(item => {
                            if (item.id === "FREE_SIZE" || item.p_code === "FREE_SIZE" || item.pbox_code === "FREE SIZE / LOSE QTY") return false;
                            const itemGrams = convertToGrams(item.p_weight, item.pbox_unit);
                            return Math.abs(itemGrams - coverGrams) <= 1;
                        });
                    }
                }

                if (!matchedItem) {
                    toast.error(`Cannot distribute: Selected packaging material "${mat?.title || 'Material'}" does not match any configured package size for this product. Please select a valid material or configure a matching package size.`);
                    return;
                }

                const targetPdId = matchedItem ? matchedItem.id : (searchList[0]?.id || "FREE_SIZE");
                const qtyToAdd = calcs.usablePcs > 0 ? calcs.usablePcs : calcs.calculatedPcs;
                const weightPerPktKg = qtyToAdd > 0 ? (calcs.qtyInKg / qtyToAdd) : calcs.qtyInKg;

                distributedAllocationItems.push({
                    id: targetPdId,
                    qty: qtyToAdd,
                    weightKg: calcs.qtyInKg,
                    packetWeightKg: weightPerPktKg
                });
            } else {
                // No pack material -> Free size / loose
                let freeSizeItem = (availableProductDetails || []).find(item => item.id === "FREE_SIZE" || item.p_code === "FREE_SIZE" || item.pbox_code === "FREE SIZE / LOSE QTY");
                const targetPdId = freeSizeItem ? freeSizeItem.id : "FREE_SIZE";

                distributedAllocationItems.push({
                    id: targetPdId,
                    qty: calcs.qtyInKg,
                    weightKg: calcs.qtyInKg,
                    packetWeightKg: 1
                });
            }
        });

        setIsSubmitting(true);
        showLoader();
        try {
            // Sort batches oldest first (FIFO based on batch_no)
            const sortedBatches = [...selectedBatch.batches].sort((a, b) => {
                return String(a.batch_no).localeCompare(String(b.batch_no));
            });

            // Initialize capacity for each batch in kg
            sortedBatches.forEach(b => {
                b.capacity = parseFloat(b.remaining || 0);
                b.allocatedItems = [];
            });

            // Loop through each distribution item and allocate to batches in FIFO order
            distributedAllocationItems.forEach(item => {
                let qtyToAllocate = parseFloat(item.qty || 0);
                if (qtyToAllocate <= 0) return;

                const packetWeightKg = item.packetWeightKg || 1;

                for (let i = 0; i < sortedBatches.length; i++) {
                    if (qtyToAllocate <= 0) break;
                    const batch = sortedBatches[i];
                    if (batch.capacity <= 0) continue;

                    let fitPackets = Math.floor(batch.capacity / packetWeightKg);
                    if (packetWeightKg === 1) {
                        fitPackets = Math.min(qtyToAllocate, batch.capacity);
                    }

                    let allocated = Math.min(qtyToAllocate, fitPackets);
                    if (allocated > 0) {
                        batch.allocatedItems.push({ id: item.id, qty: allocated });
                        batch.capacity = parseFloat((batch.capacity - allocated * packetWeightKg).toFixed(4));
                        qtyToAllocate -= allocated;
                    }
                }

                if (qtyToAllocate > 0) {
                    const targetBatch = sortedBatches.find(b => b.capacity > 0) || sortedBatches[0];
                    const existing = targetBatch.allocatedItems.find(x => x.id === item.id);
                    if (existing) {
                        existing.qty += qtyToAllocate;
                    } else {
                        targetBatch.allocatedItems.push({ id: item.id, qty: qtyToAllocate });
                    }
                    targetBatch.capacity = parseFloat((targetBatch.capacity - qtyToAllocate * packetWeightKg).toFixed(4));
                    qtyToAllocate = 0;
                }
            });

            const batchesToCall = sortedBatches.filter(b => b.allocatedItems && b.allocatedItems.length > 0);

            for (let idx = 0; idx < batchesToCall.length; idx++) {
                const batch = batchesToCall[idx];
                const isFirst = idx === 0;

                const batch_distribution = batch.allocatedItems.map(item => ({
                    id: item.id,
                    qty: Number(item.qty)
                }));

                const payload = {
                    product_transaction_id: batch.id,
                    batch_distribution,
                    packaging_materials: isFirst ? packagingMaterialsPayload : [],
                    other_charges: isFirst ? parseFloat(otherChargesVal || 0) : 0,
                    package_charges: isFirst ? parseFloat(packageCharges || 0) : 0,
                    eb_rent_charges: isFirst ? parseFloat(ebRentCharges || 0) : 0,
                    labour_charges: isFirst ? parseFloat(labourCharges || 0) : 0,
                    custom_expenses: isFirst ? customExpenses.map(item => ({
                        title: item.description,
                        amount: parseFloat(item.amount || 0)
                    })) : [],
                    created_by: UserItem?.user_id || "1",
                    shop_id: shopItem?.shop_id || shopItem?.id || "0",
                    branch_id: UserItem?.user_id || "1",
                    warehouse_id: warehouse?.id || "1"
                };

                const res = await distribute_undistributed_product(payload);
                if (!res.data || res.data.status !== "success") {
                    throw new Error(res.data?.message || `Failed to distribute batch ${batch.batch_no}`);
                }
            }

            toast.success("Product distributed successfully!");
            setShowDistributeModal(false);
            fetchUndistributedProducts(currentPage);
            fetchRawMaterials();
        } catch (error) {
            console.error("Error distributing product:", error);
            const errorMsg = error?.response?.data?.message || error.message || "An error occurred during distribution";
            toast.error(errorMsg);
        } finally {
            hideLoader();
            setIsSubmitting(false);
        }
    };

    const renderTransferModal = () => {
        if (!showTransferModal || !transferSourceBatch) return null;

        const b = transferSourceBatch;
        const prod = transferSourceProduct;
        const avl = parseFloat(b.remaining || 0);
        const sourcePId = b.p_id || prod?.p_id;

        // Destination product options excluding source product
        const targetOptions = productFilterOptions.filter(
            opt => String(opt.value) !== String(sourcePId)
        );

        const qtyNum = parseFloat(transferQty) || 0;
        const remainingKg = Math.max(0, avl - qtyNum);
        const isQtyValid = qtyNum > 0 && qtyNum <= (avl + 0.0001);

        const handlePresetPercent = (pct) => {
            const calculated = parseFloat(((avl * pct) / 100).toFixed(2));
            setTransferQty(calculated.toString());
            if (pct === 100) {
                setTransferType("full");
            } else {
                setTransferType("partial");
            }
        };

        return (
            <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1060 }}>
                <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '620px' }}>
                    <div className="modal-content shadow-lg border-0" style={{ borderRadius: '16px', overflow: 'hidden' }}>

                        {/* Modal Header */}
                        <div
                            className="modal-header py-3 px-4 text-white"
                            style={{
                                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                                borderTopLeftRadius: '16px',
                                borderTopRightRadius: '16px'
                            }}
                        >
                            <div className="d-flex align-items-center gap-2">
                                <div
                                    className="d-flex align-items-center justify-content-center rounded-circle"
                                    style={{ width: '36px', height: '36px', backgroundColor: 'rgba(255,255,255,0.2)' }}
                                >
                                    <i className="bi bi-arrow-left-right fs-5 text-white"></i>
                                </div>
                                <div>
                                    <h5 className="modal-title fw-bold m-0" style={{ fontSize: '18px' }}>
                                        Transfer End Product Quantity
                                    </h5>
                                    <small style={{ opacity: 0.9, fontSize: '12px' }}>
                                        Transfer full or partial bulk stock to another product under the same batch
                                    </small>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn-close btn-close-white ms-auto"
                                onClick={() => {
                                    if (!isTransferring) {
                                        setShowTransferModal(false);
                                        setTransferSourceBatch(null);
                                        setTransferSourceProduct(null);
                                    }
                                }}
                                disabled={isTransferring}
                            ></button>
                        </div>

                        {/* Modal Body */}
                        <div className="modal-body p-4 text-start" style={{ backgroundColor: '#f8fafc' }}>
                            {transferError && (
                                <div className="alert alert-danger py-2 px-3 small d-flex align-items-center gap-2 mb-3 rounded-3">
                                    <i className="bi bi-exclamation-triangle-fill"></i>
                                    <span>{transferError}</span>
                                </div>
                            )}

                            {/* Source Product Details Card */}
                            <div
                                className="card border-0 mb-3 shadow-sm"
                                style={{
                                    borderRadius: '12px',
                                    background: '#ffffff',
                                    border: '1px solid #e2e8f0'
                                }}
                            >
                                <div className="card-body p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="badge px-2.5 py-1 text-uppercase fw-semibold" style={{ backgroundColor: '#e0f2fe', color: '#0369a1', fontSize: '11px', letterSpacing: '0.5px' }}>
                                            Source Product (Origin)
                                        </span>
                                        <span className="badge px-2.5 py-1 fw-bold" style={{ backgroundColor: '#f1f5f9', color: '#334155', fontSize: '12px' }}>
                                            <i className="bi bi-upc-scan me-1"></i>
                                            Batch #{b.batch_no || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-baseline">
                                        <div>
                                            <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: '16px' }}>
                                                {prod?.product_title || b.product_title || 'Product'}
                                            </h6>
                                            {prod?.batches && prod.batches.length > 1 && (
                                                <div className="mt-2">
                                                    <label className="text-muted small d-block mb-1">Select Batch:</label>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={b.batch_no}
                                                        onChange={(e) => {
                                                            const selected = prod.batches.find(x => x.batch_no === e.target.value);
                                                            if (selected) {
                                                                setTransferSourceBatch(selected);
                                                                setTransferQty("");
                                                            }
                                                        }}
                                                        disabled={isTransferring}
                                                    >
                                                        {prod.batches.map(batch => (
                                                            <option key={batch.id} value={batch.batch_no}>
                                                                Batch #{batch.batch_no} ({parseFloat(batch.remaining || 0).toFixed(2)} kg available)
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                        <div className="text-end">
                                            <small className="text-muted d-block" style={{ fontSize: '11px' }}>AVAILABLE STOCK</small>
                                            <span className="fw-bold fs-5 text-primary">
                                                {avl.toFixed(2)} <span className="fs-6 fw-normal">kg</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Destination Product Selection */}
                            <div className="mb-3">
                                <label className="form-label fw-semibold small text-secondary d-flex justify-content-between align-items-center mb-1">
                                    <span>Target End Product <span className="text-danger">*</span></span>
                                    <span className="text-muted" style={{ fontSize: '11px' }}>Will inherit Batch #{b.batch_no}</span>
                                </label>
                                <Select
                                    options={targetOptions}
                                    value={transferTargetProduct}
                                    onChange={(option) => setTransferTargetProduct(option)}
                                    placeholder="Search and select destination end product..."
                                    isClearable={false}
                                    isSearchable={true}
                                    styles={filterProductSelectStyles}
                                    menuShouldScrollIntoView={false}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    noOptionsMessage={() => "No other products available"}
                                    isDisabled={isTransferring}
                                />
                                <small className="text-muted d-block mt-1" style={{ fontSize: '11.5px' }}>
                                    <i className="bi bi-info-circle me-1"></i>
                                    The destination product will be created or credited with <strong>the exact same batch number ({b.batch_no})</strong> in Distribute state.
                                </small>
                            </div>

                            {/* Transfer Date */}
                            <div className="mb-3">
                                <label className="form-label fw-semibold small text-secondary d-flex justify-content-between align-items-center mb-1">
                                    <span>Transfer Date <span className="text-danger">*</span></span>
                                    <span className="text-muted" style={{ fontSize: '11px' }}>Date of bulk stock transfer</span>
                                </label>
                                <FormattedDateInput
                                    name="transferDate"
                                    value={transferDate}
                                    onChange={(e) => setTransferDate(e.target.value)}
                                    disabled={isTransferring}
                                    required={true}
                                />
                                <small className="text-muted d-block mt-1" style={{ fontSize: '11.5px' }}>
                                    <i className="bi bi-calendar-event me-1"></i>
                                    Default is set to current date ({formatDateDisplay(getFormattedCurrentDate2())}). Change if recording transfer for a different date.
                                </small>
                            </div>

                            {/* Transfer Mode & Quantity */}
                            <div className="card border-0 mb-3 shadow-sm" style={{ borderRadius: '12px', background: '#ffffff', border: '1px solid #e2e8f0' }}>
                                <div className="card-body p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="fw-semibold small text-secondary">Transfer Mode & Quantity</span>
                                        <div className="btn-group btn-group-sm" role="group">
                                            <button
                                                type="button"
                                                className={`btn btn-sm px-3 ${transferType === 'partial' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                onClick={() => {
                                                    setTransferType('partial');
                                                    if (parseFloat(transferQty) >= avl) {
                                                        setTransferQty((avl / 2).toFixed(2));
                                                    }
                                                }}
                                                disabled={isTransferring}
                                            >
                                                Partial
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm px-3 ${transferType === 'full' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                onClick={() => {
                                                    setTransferType('full');
                                                    setTransferQty(avl.toFixed(2));
                                                }}
                                                disabled={isTransferring}
                                            >
                                                Full (100%)
                                            </button>
                                        </div>
                                    </div>

                                    <div className="mb-2">
                                        <div className="input-group">
                                            <span className="input-group-text bg-light text-secondary fw-semibold">
                                                Qty to Transfer
                                            </span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0.01"
                                                max={avl}
                                                className={`form-control fw-bold fs-6 ${!isQtyValid && transferQty ? 'is-invalid' : ''}`}
                                                placeholder={`0.00 (Max: ${avl.toFixed(2)})`}
                                                value={transferQty}
                                                onWheel={(e) => e.currentTarget.blur()}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setTransferQty(val);
                                                    if (parseFloat(val) >= avl && avl > 0) {
                                                        setTransferType('full');
                                                    } else {
                                                        setTransferType('partial');
                                                    }
                                                }}
                                                disabled={isTransferring || transferType === 'full'}
                                            />
                                            <span className="input-group-text bg-light fw-bold text-muted">kg</span>
                                        </div>
                                        {!isQtyValid && transferQty && (
                                            <small className="text-danger d-block mt-1">
                                                Quantity must be between 0.01 and {avl.toFixed(2)} kg
                                            </small>
                                        )}
                                    </div>

                                    {/* Quick Percent Presets */}
                                    <div className="d-flex align-items-center gap-1.5 flex-wrap pt-1">
                                        <small className="text-muted me-1" style={{ fontSize: '11px' }}>Quick:</small>
                                        {[25, 50, 75, 100].map(pct => (
                                            <button
                                                key={pct}
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm py-0 px-2 rounded-pill"
                                                style={{ fontSize: '11px' }}
                                                onClick={() => handlePresetPercent(pct)}
                                                disabled={isTransferring}
                                            >
                                                {pct}%
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Live Balance Summary */}
                            <div className="p-3 mb-3 rounded-3" style={{ backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1' }}>
                                <div className="row g-2 text-center">
                                    <div className="col-4">
                                        <small className="d-block text-muted text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                                            Source Remaining
                                        </small>
                                        <strong className={`d-block fs-6 ${remainingKg === 0 ? 'text-secondary' : 'text-dark'}`}>
                                            {remainingKg.toFixed(2)} kg
                                        </strong>
                                    </div>
                                    <div className="col-4 border-start border-end">
                                        <small className="d-block text-muted text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                                            Transferring
                                        </small>
                                        <strong className="d-block fs-6 text-primary">
                                            {qtyNum > 0 ? `${qtyNum.toFixed(2)} kg` : '0.00 kg'}
                                        </strong>
                                    </div>
                                    <div className="col-4">
                                        <small className="d-block text-muted text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                                            Target Added
                                        </small>
                                        <strong className="d-block fs-6 text-success">
                                            +{qtyNum > 0 ? `${qtyNum.toFixed(2)} kg` : '0.00 kg'}
                                        </strong>
                                    </div>
                                </div>
                            </div>

                            {/* Remarks */}
                            <div className="mb-0">
                                <label className="form-label fw-semibold small text-secondary mb-1">
                                    Remarks / Transfer Note
                                </label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="e.g. Transferred for repackaging under same batch"
                                    value={transferRemarks}
                                    onChange={(e) => setTransferRemarks(e.target.value)}
                                    disabled={isTransferring}
                                />
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="modal-footer bg-light px-4 py-3 d-flex justify-content-between align-items-center">
                            <button
                                type="button"
                                className="btn btn-outline-secondary px-3 rounded-pill"
                                onClick={() => {
                                    setShowTransferModal(false);
                                    setTransferSourceBatch(null);
                                    setTransferSourceProduct(null);
                                }}
                                disabled={isTransferring}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary px-4 rounded-pill shadow-sm d-flex align-items-center gap-2"
                                style={{ backgroundColor: '#0284c7', borderColor: '#0284c7' }}
                                onClick={handleTransferSubmit}
                                disabled={isTransferring || !isQtyValid || !transferTargetProduct || !transferDate}
                            >
                                {isTransferring ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        <span>Transferring...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-arrow-left-right"></i>
                                        <span>Confirm Transfer</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const isDistributing = Boolean(showDistributeModal && selectedBatch);

    return (
        <div className="page-wrappers">
            <style>{`
              .form-control.packaging-field-disabled:disabled,
              .form-control.packaging-field-disabled[readonly] {
                background-color: rgba(233, 236, 239, 1) !important;
                color: #4a4e52 !important;
              }
            `}</style>
            <div
                className="page-content"
                style={isDistributing ? {
                    minHeight: "calc(100vh - 65px)",
                    display: "flex",
                    flexDirection: "column",
                    padding: "0.5rem 1.25rem"
                } : {}}
            >
                <div className="flex-shrink-0">
                    <NaviGate />
                    <div className="mb-1 d-flex flex-wrap justify-content-between align-items-center gap-2">
                        {isDistributing ? (
                            <>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <h5 className="mb-0 fw-bold text-dark text-nowrap" style={{ fontFamily: "poppins" }}>
                                        {modalStep === 1 ? "Create Process Details" : modalStep === 2 ? "Manufacturing Details" : "Product Distribution"}
                                    </h5>
                                    <span
                                        className="badge bg-primary py-2 px-3 text-wrap text-start"
                                        style={{ maxWidth: "450px" }}
                                        title={selectedBatch.batch_no}
                                    >
                                        Batch #{formatBatchNo(selectedBatch.batches)} - {modalStep === 1 ? 'Creation' : modalStep === 2 ? 'Manufacturing' : 'Distribution'} Step
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary bg-white d-flex align-items-center gap-1.5 rounded-pill shadow-sm px-3 py-1"
                                        onClick={() => {
                                            handleOpenTransferModal(selectedBatch);
                                        }}
                                        title="Transfer full or partial quantity to another end product under the same batch"
                                    >
                                        <i className="bi bi-arrow-left-right text-primary"></i>
                                        <span className="fw-semibold small">Transfer to Another Product</span>
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowDistributeModal(false)}
                                    title="Close"
                                    disabled={isSubmitting}
                                    style={{ cursor: "pointer", fontSize: "0.95rem" }}
                                ></button>
                            </>
                        ) : (
                            <>
                                <h6 className="mb-0 text-uppercase text-nowrap" style={{ fontFamily: "poppins" }}>
                                    Undistributed Product Management
                                </h6>
                                <div style={{ width: "280px", maxWidth: "100%" }} title={selectedFilterProduct?.label || "Filter by product"}>
                                    <Select
                                        options={productFilterOptions}
                                        value={selectedFilterProduct}
                                        onChange={(selected) => {
                                            setSelectedFilterProduct(selected);
                                            setCurrentPage(1);
                                        }}
                                        placeholder="Filter by product..."
                                        isClearable={true}
                                        isSearchable={true}
                                        styles={filterProductSelectStyles}
                                        menuPortalTarget={document.body}
                                        noOptionsMessage={() => "No products found"}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    <hr className="my-1" />
                </div>

                {isDistributing ? (
                    <div className="undistributed-page-content flex-grow-1">
                        <ProcessStepBar currentStep={modalStep} onStepClick={setModalStep} />

                        <form onSubmit={handleSubmitDistribution}>
                            {modalStep === 1 && renderStep1Content()}
                            {modalStep === 2 && renderStep2Content()}
                            {modalStep === 3 && renderStep3Content()}
                        </form>
                    </div>
                ) : (
                    <div className="card">
                        <div className="card-body">


                            {/* List Table */}
                            <div className="table-responsive">
                                <table className="table table-striped table-sm table-bordered" style={{ width: "100%", fontFamily: "poppins" }}>
                                    <thead>
                                        <tr>
                                            <th>S.No</th>
                                            <th>Product Name</th>
                                            {/* <th>Batch No</th> */}
                                            <th>Mfg Qty</th>
                                            <th>Remaining Bulk Qty</th>
                                            <th>Mfg Date</th>
                                            <th>Expiry Date</th>
                                            <th className="text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            const calculatedTotalPages = Math.max(1, Math.ceil(filteredGroupedProducts.length / entriesPerPage));
                                            const startIndex = (currentPage - 1) * entriesPerPage;
                                            const paginatedGroupedProducts = filteredGroupedProducts.slice(startIndex, startIndex + entriesPerPage);

                                            if (paginatedGroupedProducts.length === 0) {
                                                return (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-3">No undistributed bulk products found</td>
                                                    </tr>
                                                );
                                            }

                                            return paginatedGroupedProducts.map((prod, index) => (
                                                <tr key={prod.p_id}>
                                                    <td>{startIndex + index + 1}</td>
                                                    <td className="fw-semibold">
                                                        <div>{prod.product_title || "Unknown Product"}</div>
                                                        <div className="d-flex flex-column align-items-center gap-1 mt-2">
                                                            {prod.batches.map(b => (
                                                                <span key={b.id} className="badge bg-light text-dark border fw-normal" style={{ fontSize: "0.75rem" }}>
                                                                    {b.batch_no}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td>{Number(prod.total_qty).toFixed(2)} {prod.units || "kg"}</td>
                                                    <td>
                                                        <span className="badge bg-light text-success border fw-bold">
                                                            {Number(prod.total_remaining).toFixed(3)} {prod.units || "kg"}
                                                        </span>
                                                    </td>
                                                    <td>{getMfgDateDisplay(prod.mfg_dates)}</td>
                                                    <td>{getExpiryDateDisplay(prod.expiry_dates)}</td>
                                                    <td className="text-center">
                                                        <div className="d-flex align-items-center justify-content-center gap-1.5 flex-nowrap">
                                                            <button
                                                                className="btn btn-sm btn-warning text-dark px-3 py-1 rounded-pill shadow-sm border-0 d-inline-flex align-items-center gap-1"
                                                                onClick={() => handleOpenDistributeModal(prod)}
                                                                title="Distribute product into packet sizes"
                                                            >
                                                                <i className="bi bi-box-arrow-up-right"></i>
                                                                <span className="fw-bold small">Distribute</span>
                                                            </button>
                                                            <button
                                                                className="btn btn-link p-0 border-0 ms-2 d-flex align-items-center justify-content-center"
                                                                style={{ fontSize: "1.25rem", textDecoration: "none", color: "#0284c7" }}
                                                                type="button"
                                                                onClick={() => handleOpenTransferModal(prod)}
                                                                title="Transfer full or partial quantity to other end products under same batch"
                                                            >
                                                                <i className="bi bi-arrow-left-right"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ));
                                        })()}
                                    </tbody>
                                </table>

                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={Math.max(1, Math.ceil(filteredGroupedProducts.length / entriesPerPage))}
                                    onPageChange={handlePageChange}
                                    entriesPerPage={entriesPerPage}
                                    onEntriesPerPageChange={(val) => {
                                        setEntriesPerPage(val);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Product Details Modal */}
                {showDetailsModal && (
                    <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }} tabIndex="-2">
                        <div className="modal-dialog modal-lg">
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "16px" }}>
                                <div className="modal-header bg-dark text-white border-0" style={{ borderTopLeftRadius: "16px", borderTopRightRadius: "16px" }}>
                                    <h5 className="modal-title d-flex align-items-center gap-2">
                                        <i className="bi bi-plus-circle-fill"></i>
                                        <span>Add Product Package Details</span>
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => {
                                            setShowDetailsModal(false);
                                            resetProductDetailsForm();
                                        }}
                                    ></button>
                                </div>

                                <div className="modal-body p-4 text-start">
                                    <form onSubmit={handleProductDetailsSubmit}>
                                        <div className="row g-3">
                                            {/* Product Selection */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">Select Product <span className="text-danger">*</span></label>
                                                <select
                                                    name="p_id"
                                                    value={formData1.p_id}
                                                    className={`form-control ${errorss.p_id ? "is-invalid" : ""}`}
                                                    disabled
                                                >
                                                    <option value={selectedBatch?.p_id}>{selectedBatch?.product_title}</option>
                                                </select>
                                                {errorss.p_id && <div className="invalid-feedback">{errorss.p_id}</div>}
                                            </div>

                                            {/* Product Code */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">Product Code</label>
                                                <input
                                                    type="text"
                                                    name="p_code"
                                                    value={formData1.p_code}
                                                    onChange={(e) => setFormData1(prev => ({ ...prev, p_code: e.target.value }))}
                                                    className="form-control"
                                                />
                                            </div>

                                            {/* Product Weight */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">Weight <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    name="p_weight"
                                                    value={formData1.p_weight}
                                                    onChange={(e) => setFormData1(prev => ({ ...prev, p_weight: e.target.value }))}
                                                    className={`form-control ${errorss.p_weight ? "is-invalid" : ""}`}
                                                    placeholder="0.00"
                                                />
                                                {errorss.p_weight && <div className="invalid-feedback">{errorss.p_weight}</div>}
                                            </div>

                                            {/* Unit */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">Unit <span className="text-danger">*</span></label>
                                                <select
                                                    name="pbox_unit"
                                                    value={formData1.pbox_unit}
                                                    onChange={(e) => setFormData1(prev => ({ ...prev, pbox_unit: e.target.value }))}
                                                    className={`form-control ${errorss.pbox_unit ? "is-invalid" : ""}`}
                                                >
                                                    <option value="">Select Unit</option>
                                                    <option value="grams">Grams</option>
                                                    <option value="kilograms">Kilograms</option>
                                                </select>
                                                {errorss.pbox_unit && <div className="invalid-feedback">{errorss.pbox_unit}</div>}
                                            </div>

                                            {/* Price */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">Price <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    name="p_price"
                                                    value={formData1.p_price}
                                                    onChange={(e) => setFormData1(prev => ({ ...prev, p_price: e.target.value }))}
                                                    className={`form-control ${errorss.p_price ? "is-invalid" : ""}`}
                                                    placeholder="0.00"
                                                />
                                                {errorss.p_price && <div className="invalid-feedback">{errorss.p_price}</div>}
                                            </div>

                                            {/* Box Quantity */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">Box Quantity</label>
                                                <input
                                                    type="number"
                                                    name="pbox_qty"
                                                    value={formData1.pbox_qty}
                                                    onChange={(e) => setFormData1(prev => ({ ...prev, pbox_qty: e.target.value }))}
                                                    onWheel={(e) => e.currentTarget.blur()}
                                                    className={`form-control ${errorss.pbox_qty ? "is-invalid" : ""}`}
                                                />
                                                {errorss.pbox_qty && <div className="invalid-feedback">{errorss.pbox_qty}</div>}
                                            </div>

                                            {/* Box Code */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">Box Code</label>
                                                <input
                                                    type="text"
                                                    name="pbox_code"
                                                    value={formData1.pbox_code}
                                                    onChange={(e) => setFormData1(prev => ({ ...prev, pbox_code: e.target.value }))}
                                                    className="form-control"
                                                />
                                            </div>

                                            {/* Box Price */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-bold">Box Price</label>
                                                <input
                                                    type="number"
                                                    name="pbox_price"
                                                    value={formData1.pbox_price}
                                                    onChange={(e) => setFormData1(prev => ({ ...prev, pbox_price: e.target.value }))}
                                                    onWheel={(e) => e.currentTarget.blur()}
                                                    className="form-control"
                                                    step="1"
                                                />
                                            </div>
                                        </div>

                                        <div className="modal-footer border-0 p-0 mt-4 d-flex gap-2 justify-content-end">
                                            <button
                                                type="button"
                                                className="btn btn-secondary px-4"
                                                onClick={() => {
                                                    setShowDetailsModal(false);
                                                    resetProductDetailsForm();
                                                }}
                                            >
                                                Cancel
                                            </button>
                                            <button type="submit" className="btn btn-primary px-4" disabled={loading}>
                                                {loading ? "Saving..." : "Save Details"}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {renderTransferModal()}
            </div>
        </div>
    );
};

export default UndistributedProducts;
